import { supabase, supabaseUrl, supabaseAnonKey } from './supabase';
import { normalizeClockValue, timeToMinutes } from './plannerPriorityValidation';

export const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PLANNER_DATE_TIME_WITHOUT_TIMEZONE_REGEX = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/;
const PRAYER_SETTINGS_CACHE_PREFIX = 'bereket-prayer-settings-v2';
const PRAYER_SETTING_KEYS = [
  'fajr_adjustment',
  'dhuhr_adjustment',
  'asr_adjustment',
  'maghrib_adjustment',
  'isha_adjustment',
];

const padPlannerDatePart = (value) => String(value).padStart(2, '0');

const buildPlannerUTCISOString = ({ year, month, day, hours = 0, minutes = 0, seconds = 0 }) => new Date(Date.UTC(
  Number.isFinite(year) ? year : 1970,
  (Number.isFinite(month) ? month : 1) - 1,
  Number.isFinite(day) ? day : 1,
  Number.isFinite(hours) ? hours : 0,
  Number.isFinite(minutes) ? minutes : 0,
  Number.isFinite(seconds) ? seconds : 0,
  0,
)).toISOString();

const hasExplicitPlannerTimezone = (value = '') => /(?:Z|[+-]\d{2}:\d{2})$/i.test(String(value).trim());

export const normalizePlannerDateTimeValue = (value) => {
  if (!value) return value;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  }

  const raw = String(value).trim();
  if (!raw) return raw;

  const plainMatch = raw.match(PLANNER_DATE_TIME_WITHOUT_TIMEZONE_REGEX);
  if (plainMatch && !hasExplicitPlannerTimezone(raw)) {
    const [, year, month, day, hours, minutes, seconds] = plainMatch;
    return buildPlannerUTCISOString({
      year: Number(year),
      month: Number(month),
      day: Number(day),
      hours: Number(hours),
      minutes: Number(minutes),
      seconds: Number(seconds || '0'),
    });
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  return parsed.toISOString();
};

export const formatPlannerDateTimeLocalInput = (value) => {
  if (!value) return '';

  const normalized = normalizePlannerDateTimeValue(value);
  if (!normalized) return '';

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return String(value).slice(0, 16);
  }

  return `${parsed.getUTCFullYear()}-${padPlannerDatePart(parsed.getUTCMonth() + 1)}-${padPlannerDatePart(parsed.getUTCDate())}T${padPlannerDatePart(parsed.getUTCHours())}:${padPlannerDatePart(parsed.getUTCMinutes())}`;
};

const normalizePlannerEventRecord = (event = {}) => ({
  ...event,
  start_time: normalizePlannerDateTimeValue(event.start_time),
  end_time: normalizePlannerDateTimeValue(event.end_time),
});

const createTodayPlanPriorityId = (index = 0) => `priority-${index + 1}-${Math.random().toString(36).slice(2, 8)}`;

const getDefaultPriorityTimeRange = (index = 0) => ({
  start_time: `${String(9 + index).padStart(2, '0')}:00`,
  end_time: `${String(10 + index).padStart(2, '0')}:00`,
});

export const createEmptyTodayPlanPriority = (index = 0, options = {}) => {
  const { withDefaultTime = false } = options;
  const defaultRange = getDefaultPriorityTimeRange(index);

  return {
    id: createTodayPlanPriorityId(index),
    title: '',
    goal_niyyah: '',
    start_time: withDefaultTime ? defaultRange.start_time : '',
    end_time: withDefaultTime ? defaultRange.end_time : '',
    reminder_minutes: null,
    blocked_event_id: null,
    blocked_start_time: null,
  };
};

export const hasPriorityTimeRange = (priority = {}) => {
  const startMinutes = timeToMinutes(priority?.start_time);
  const endMinutes = timeToMinutes(priority?.end_time);
  return startMinutes !== null && endMinutes !== null;
};

export const hasAnyPriorityTimeValue = (priority = {}) => (
  timeToMinutes(priority?.start_time) !== null || timeToMinutes(priority?.end_time) !== null
);

export const hasTodayPlanContent = (plan = null) => {
  if (!plan) return false;
  if (String(plan?.niyyah || '').trim()) return true;

  return (plan?.priorities || []).some((priority) => (
    String(priority?.title || '').trim()
    || String(priority?.goal_niyyah || '').trim()
    || hasAnyPriorityTimeValue(priority)
  ));
};

export const createDefaultTodayPlan = (date = getLocalDateString()) => ({
  date,
  niyyah: '',
  priorities: [createEmptyTodayPlanPriority(0)],
  avoid_distraction: '',
  notes: '',
  plan_items: [],
});

const getPrayerSettingsCacheKey = (userId) => `${PRAYER_SETTINGS_CACHE_PREFIX}:${userId}`;

const readCachedPrayerSettings = (userId) => {
  if (typeof window === 'undefined' || !userId) return null;

  try {
    const raw = localStorage.getItem(getPrayerSettingsCacheKey(userId));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedPrayerSettings = (userId, settings) => {
  if (typeof window === 'undefined' || !userId || !settings) return;

  try {
    localStorage.setItem(getPrayerSettingsCacheKey(userId), JSON.stringify(settings));
  } catch {
    // no-op
  }
};

const normalizePrayerLocationValue = (value = '') => String(value || '').trim().toLowerCase().replace(/\s+/g, '_');

const hasDefaultPrayerAdjustments = (settings = null) => PRAYER_SETTING_KEYS.every((key) => Number(settings?.[key] || 0) === 0);

const shouldUsePrayerSettingsAutoApply = ({ prayerSettings, profileSettings }) => {
  const city = normalizePrayerLocationValue(prayerSettings?.default_city || profileSettings?.city || '');
  const country = normalizePrayerLocationValue(prayerSettings?.default_country || '');
  const timezone = normalizePrayerLocationValue(profileSettings?.timezone || '');

  const looksZagreb = city === 'zagreb'
    || city.includes('zagreb')
    || timezone.includes('europe/zagreb')
    || (city === 'zagreb' && country.includes('croatia'));

  return looksZagreb && hasDefaultPrayerAdjustments(prayerSettings);
};

const fetchJsonWithTimeout = async (url, options = {}, timeoutMs = 4000) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    return response;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const parseLocalDateKey = (dateString) => {
  const [year, month, day] = String(dateString).split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

export const shiftLocalDateKey = (dateString, days) => {
  const nextDate = parseLocalDateKey(dateString);
  nextDate.setDate(nextDate.getDate() + days);
  return getLocalDateString(nextDate);
};

const NOTIFICATION_TIME_KEYS = [
  'prayer_reminder_time',
  'dhikr_reminder_time',
  'verse_reminder_time',
  'reflection_reminder_time',
];

const isValidTimeValue = (time) => /^\d{2}:\d{2}$/.test(time || '');

const convertLocalTimeToUtc = (time) => {
  if (!isValidTimeValue(time)) return time;
  const [hours, minutes] = time.split(':').map(Number);
  const localDate = new Date();
  localDate.setHours(hours, minutes, 0, 0);
  const utcHours = String(localDate.getUTCHours()).padStart(2, '0');
  const utcMinutes = String(localDate.getUTCMinutes()).padStart(2, '0');
  return `${utcHours}:${utcMinutes}`;
};

const convertUtcTimeToLocal = (time) => {
  if (!isValidTimeValue(time)) return time;
  const [hours, minutes] = time.split(':').map(Number);
  const utcDate = new Date();
  utcDate.setUTCHours(hours, minutes, 0, 0);
  const localHours = String(utcDate.getHours()).padStart(2, '0');
  const localMinutes = String(utcDate.getMinutes()).padStart(2, '0');
  return `${localHours}:${localMinutes}`;
};

const getNotificationUtcStorageFlag = () => {
  try {
    return localStorage.getItem('bereket_notification_times_utc_v1') === 'true';
  } catch {
    return false;
  }
};

const setNotificationUtcStorageFlag = () => {
  try {
    localStorage.setItem('bereket_notification_times_utc_v1', 'true');
  } catch {
    // no-op
  }
};

const TABLE_AVAILABILITY_STORAGE_KEY = 'bereket-table-availability-v1';
const DEFAULT_TABLE_AVAILABILITY = {
  daily_barakah_plans: false,
};

const getTableAvailabilityState = () => {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_TABLE_AVAILABILITY };
  }

  try {
    const raw = localStorage.getItem(TABLE_AVAILABILITY_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return { ...DEFAULT_TABLE_AVAILABILITY, ...(parsed || {}) };
  } catch {
    return { ...DEFAULT_TABLE_AVAILABILITY };
  }
};

let tableAvailabilityState = getTableAvailabilityState();

const isTableAvailabilityEnabled = (tableName) => tableAvailabilityState[tableName] !== false;

const setTableAvailability = (tableName, isEnabled) => {
  tableAvailabilityState = {
    ...tableAvailabilityState,
    [tableName]: isEnabled,
  };

  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(TABLE_AVAILABILITY_STORAGE_KEY, JSON.stringify(tableAvailabilityState));
  } catch {
    // no-op
  }
};

let quranReadingProgressSchemaPreference = 'legacy';

const normalizeQuranProgressToAyahList = (rows = []) => {
  const uniqueAyahs = new Set();

  rows.forEach((row) => {
    const surah = Number(row?.surah_number);
    if (!Number.isFinite(surah)) return;

    const ayahSingle = Number(row?.ayah_number);
    if (Number.isFinite(ayahSingle)) {
      uniqueAyahs.add(`${surah}:${ayahSingle}`);
      return;
    }

    const ayahStart = Number(row?.ayah_start);
    const ayahEnd = Number(row?.ayah_end);
    if (Number.isFinite(ayahStart) && Number.isFinite(ayahEnd) && ayahEnd >= ayahStart) {
      const span = Math.min(ayahEnd - ayahStart + 1, 500);
      for (let i = 0; i < span; i += 1) {
        uniqueAyahs.add(`${surah}:${ayahStart + i}`);
      }
    }
  });

  return Array.from(uniqueAyahs).map((key) => {
    const [surah_number, ayah_number] = key.split(':').map(Number);
    return { surah_number, ayah_number };
  });
};

export const getPrayersToday = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const today = getLocalDateString();
  const { data, error } = await supabase
    .from('prayers')
    .select('*')
    .eq('user_id', user.id)
    .eq('date', today)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') throw error;

  return data || {
    fajr: false,
    dhuhr: false,
    asr: false,
    maghrib: false,
    isha: false
  };
};

export const getDhikrToday = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const today = getLocalDateString();
  const { data, error } = await supabase
    .from('dhikr_counts')
    .select('*')
    .eq('user_id', user.id)
    .eq('date', today)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') throw error;

  return data || { count: 0, date: today };
};

export const incrementDhikr = async (increment = 1) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const today = getLocalDateString();

  const { data: existing } = await supabase
    .from('dhikr_counts')
    .select('*')
    .eq('user_id', user.id)
    .eq('date', today)
    .maybeSingle();

  if (existing) {
    const { data, error } = await supabase
      .from('dhikr_counts')
      .update({ count: existing.count + increment })
      .eq('id', existing.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase
      .from('dhikr_counts')
      .insert([{
        user_id: user.id,
        date: today,
        count: increment
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

export const resetDhikr = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const today = getLocalDateString();

  const { data: existing } = await supabase
    .from('dhikr_counts')
    .select('id')
    .eq('user_id', user.id)
    .eq('date', today)
    .maybeSingle();

  if (existing) {
    const { data, error } = await supabase
      .from('dhikr_counts')
      .update({ count: 0 })
      .eq('id', existing.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  return { count: 0, date: today };
};

export const getDailySummary = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  const today = getLocalDateString();

  if (!user) {
    return {
      total_points: 0,
      level: 'Starting the Journey',
      level_emoji: '🌾',
      prayers_completed: 0,
      dhikr_count: 0,
      activities_logged: 0,
      quran: {
        total_read: 0,
        listened_today: 0,
        total_memorized: 0,
        memorized_ayahs: 0,
        listening_minutes: 0,
        read_sessions_today: 0,
        listen_sessions_today: 0,
        total_sessions_today: 0,
        pages_read_today: 0,
        listening_minutes_today: 0,
      }
    };
  }

  const [prayersRes, dhikrRes, bereketRes, bereketActivitiesRes, memorizedRes, quranLogsRes] = await Promise.all([
    supabase.from('prayers').select('*').eq('user_id', user.id).eq('date', today).maybeSingle(),
    supabase.from('dhikr_counts').select('count').eq('user_id', user.id).eq('date', today).maybeSingle(),
    supabase.from('bereket_logs').select('points_earned, activity_id').eq('user_id', user.id).eq('date', today),
    supabase.from('bereket_logs')
      .select('activity_id, bereket_activities(name_en)')
      .eq('user_id', user.id)
      .eq('date', today),
    // Count total memorized ayahs from Hifz/reading progress
    supabase
      .from('quran_reading_progress')
      .select('*')
      .eq('user_id', user.id),
    // Get today's quran daily logs for reading/listening stats
    supabase
      .from('quran_daily_logs')
      .select('*')
      .eq('user_id', user.id)
      .eq('date', today),
  ]);

  const prayers = prayersRes.data || {};
  const prayersCompleted = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha']
    .filter(p => prayers[p]).length;

  const totalPoints = bereketRes.data?.reduce((sum, log) => sum + log.points_earned, 0) || 0;

  const activities = bereketActivitiesRes.data || [];

  const activityNameById = new Map();
  activities.forEach((row) => {
    const name = row?.bereket_activities?.name_en;
    if (row?.activity_id && name) {
      activityNameById.set(row.activity_id, String(name).toLowerCase());
    }
  });

  if (activityNameById.size === 0 && (bereketRes.data || []).length > 0) {
    const activityIds = [...new Set((bereketRes.data || []).map(log => log.activity_id).filter(Boolean))];
    if (activityIds.length > 0) {
      const { data: activityMeta } = await supabase
        .from('bereket_activities')
        .select('id, name_en')
        .in('id', activityIds);

      (activityMeta || []).forEach((row) => {
        if (row?.id && row?.name_en) {
          activityNameById.set(row.id, String(row.name_en).toLowerCase());
        }
      });
    }
  }

  const quranActivityTypeCounts = (bereketRes.data || []).reduce((acc, log) => {
    const name = activityNameById.get(log.activity_id) || '';
    if (name.includes('quran') || name.includes('kur')) {
      if (name.includes('read') || name.includes('čitan')) acc.read += 1;
      if (name.includes('listen') || name.includes('sluš')) acc.listen += 1;
      if (name.includes('memorize') || name.includes('učen') || name.includes('napamet')) acc.memorize += 1;
    }
    return acc;
  }, { read: 0, listen: 0, memorize: 0 });

  const readActivities = quranActivityTypeCounts.read;
  const listenActivities = quranActivityTypeCounts.listen;
  const memorizeActivities = quranActivityTypeCounts.memorize;

  // Hifz stats
  const memorizedAyahsTotal = normalizeQuranProgressToAyahList(memorizedRes.data || []).length;
  
  // Today's quran daily logs
  const todayQuranLogs = quranLogsRes.data || [];
  const pagesReadToday = todayQuranLogs.reduce((sum, log) => sum + (log.pages_read || 0), 0);
  const listeningMinutesFromLogs = todayQuranLogs.reduce((sum, log) => {
    const value = Number(log.listening_minutes ?? log.time_spent ?? 0);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
  const readSessionsFromLogs = todayQuranLogs.reduce((sum, log) => {
    const explicit = Number(log.read_sessions);
    if (Number.isFinite(explicit)) return sum + explicit;
    return sum + ((log.pages_read || 0) > 0 ? 1 : 0);
  }, 0);
  const listenSessionsFromLogs = todayQuranLogs.reduce((sum, log) => {
    const explicit = Number(log.listening_sessions);
    if (Number.isFinite(explicit)) return sum + explicit;
    const minutes = Number(log.listening_minutes ?? log.time_spent ?? 0);
    return sum + (minutes > 0 ? 1 : 0);
  }, 0);

  const readSessionsToday = Math.max(readActivities, readSessionsFromLogs);
  const listenSessionsToday = Math.max(listenActivities, listenSessionsFromLogs);
  const totalQuranSessionsToday = readSessionsToday + listenSessionsToday;

  let level = 'Starting the Journey';
  let levelEmoji = '🌾';

  if (totalPoints >= 100) {
    level = 'Day of Spiritual Breakthrough';
    levelEmoji = '✨';
  } else if (totalPoints >= 75) {
    level = 'Day of Barakah';
    levelEmoji = '🌟';
  } else if (totalPoints >= 50) {
    level = 'Day of Growth';
    levelEmoji = '🌱';
  } else if (totalPoints >= 25) {
    level = 'Day of Awareness';
    levelEmoji = '🌿';
  }

  return {
    total_points: totalPoints,
    level,
    level_emoji: levelEmoji,
    prayers_completed: prayersCompleted,
    dhikr_count: dhikrRes.data?.count || 0,
    activities_logged: bereketRes.data?.length || 0,
    quran: {
      total_read: readSessionsToday,
      listened_today: listenSessionsToday,
      total_memorized: memorizeActivities,
      memorized_ayahs: memorizedAyahsTotal,
      listening_minutes: listeningMinutesFromLogs,
      read_sessions_today: readSessionsToday,
      listen_sessions_today: listenSessionsToday,
      total_sessions_today: totalQuranSessionsToday,
      pages_read_today: pagesReadToday,
      listening_minutes_today: listeningMinutesFromLogs,
    }
  };
};

export const getDailyContent = async () => {
  const verses = [
    {
      text: "Indeed, Allah is with those who are patient.",
      reference: "Quran 2:153"
    },
    {
      text: "So remember Me; I will remember you.",
      reference: "Quran 2:152"
    },
    {
      text: "And whoever fears Allah - He will make for him a way out.",
      reference: "Quran 65:2"
    }
  ];

  const hadiths = [
    {
      text: "The best of you are those who are best to their families.",
      reference: "Tirmidhi"
    },
    {
      text: "The strong person is not the one who can wrestle someone else down. The strong person is the one who can control himself when he is angry.",
      reference: "Bukhari & Muslim"
    },
    {
      text: "None of you truly believes until he loves for his brother what he loves for himself.",
      reference: "Bukhari & Muslim"
    }
  ];

  const randomVerse = verses[Math.floor(Math.random() * verses.length)];
  const randomHadith = hadiths[Math.floor(Math.random() * hadiths.length)];

  return {
    verse: randomVerse,
    hadith: randomHadith
  };
};

export const updatePrayer = async (prayerName, completed) => {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) throw new Error('Not authenticated');

  const today = getLocalDateString();

  const { data: existing } = await supabase
    .from('prayers')
    .select('*')
    .eq('user_id', user.id)
    .eq('date', today)
    .maybeSingle();

  const prayerData = {
    user_id: user.id,
    date: today,
    [prayerName]: completed
  };

  if (existing) {
    const { error } = await supabase
      .from('prayers')
      .update(prayerData)
      .eq('id', existing.id);

    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('prayers')
      .insert([{ ...prayerData, fajr: false, dhuhr: false, asr: false, maghrib: false, isha: false, [prayerName]: completed }]);

    if (error) throw error;
  }
};

export const getPrayerTimes = async (city, country, day, month, year) => {
  try {
    const response = await fetch(
      `https://api.aladhan.com/v1/timingsByCity/${day}-${month}-${year}?city=${encodeURIComponent(city)}&country=${encodeURIComponent(country)}&method=3`
    );
    const result = await response.json();

    if (result.code === 200 && result.data) {
      const timings = result.data.timings;
      const date = result.data.date;

      return {
        city: result.data.meta.timezone,
        date: `${date.readable}`,
        hijri_date: `${date.hijri.day} ${date.hijri.month.en} ${date.hijri.year}`,
        timings: {
          fajr: timings.Fajr,
          sunrise: timings.Sunrise,
          dhuhr: timings.Dhuhr,
          asr: timings.Asr,
          maghrib: timings.Maghrib,
          isha: timings.Isha
        }
      };
    }

    throw new Error('Failed to fetch prayer times');
  } catch (error) {
    console.error('Prayer times API error:', error);
    throw error;
  }
};

export const getUserPrayerSettings = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const fallbackDefaults = {
    default_city: 'Sarajevo',
    default_country: 'Bosnia and Herzegovina',
    fajr_adjustment: 0,
    dhuhr_adjustment: 0,
    asr_adjustment: 0,
    maghrib_adjustment: 0,
    isha_adjustment: 0,
  };

  const { data: { session } } = await supabase.auth.getSession();
  try {
    const [prayerSettingsResult, profileResult] = await Promise.all([
      supabase
        .from('user_prayer_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle(),
      supabase
        .from('barakah_profiles')
        .select('city, timezone')
        .eq('user_id', user.id)
        .maybeSingle(),
    ]);

    if (prayerSettingsResult.error && prayerSettingsResult.error.code !== 'PGRST116') {
      throw prayerSettingsResult.error;
    }

    if (profileResult.error && profileResult.error.code !== 'PGRST116') {
      throw profileResult.error;
    }

    const directSettings = prayerSettingsResult.data || null;
    const profileSettings = profileResult.data || null;
    const mergedDirectSettings = {
      ...fallbackDefaults,
      ...(directSettings || {}),
    };

    if (!session?.access_token || !shouldUsePrayerSettingsAutoApply({ prayerSettings: directSettings, profileSettings })) {
      writeCachedPrayerSettings(user.id, mergedDirectSettings);
      return mergedDirectSettings;
    }

    const envBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
    const candidateUrls = [];

    if (envBackendUrl) {
      candidateUrls.push(`${envBackendUrl}/api/prayer/settings`);
    }

    if (typeof window !== 'undefined') {
      candidateUrls.push('/api/prayer/settings');

      if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
        candidateUrls.push('http://127.0.0.1:8001/api/prayer/settings');
      }
    }

    const uniqueUrls = [...new Set(candidateUrls)];
    for (const url of uniqueUrls) {
      try {
        const response = await fetchJsonWithTimeout(url, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
        });

        if (!response.ok) {
          continue;
        }

        const payload = await response.json();
        const mergedPayload = {
          ...fallbackDefaults,
          ...(payload || {}),
        };
        writeCachedPrayerSettings(user.id, mergedPayload);
        return mergedPayload;
      } catch {
        // Silent fallback to direct settings/cache below
      }
    }

    writeCachedPrayerSettings(user.id, mergedDirectSettings);
    return mergedDirectSettings;
  } catch (error) {
    const cachedSettings = readCachedPrayerSettings(user.id);
    if (cachedSettings) {
      return {
        ...fallbackDefaults,
        ...cachedSettings,
      };
    }

    throw error;
  }
};

export const updatePrayerSettings = async (settings) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: existing } = await supabase
    .from('user_prayer_settings')
    .select('id')
    .eq('user_id', user.id)
    .maybeSingle();

  if (existing) {
    const { data, error } = await supabase
      .from('user_prayer_settings')
      .update(settings)
      .eq('id', existing.id)
      .select()
      .single();

    if (error) throw error;
    writeCachedPrayerSettings(user.id, {
      ...PRAYER_SETTING_KEYS.reduce((acc, key) => ({ ...acc, [key]: 0 }), {}),
      ...data,
    });
    return data;
  } else {
    const { data, error } = await supabase
      .from('user_prayer_settings')
      .insert([{ user_id: user.id, ...settings }])
      .select()
      .single();

    if (error) throw error;
    writeCachedPrayerSettings(user.id, {
      ...PRAYER_SETTING_KEYS.reduce((acc, key) => ({ ...acc, [key]: 0 }), {}),
      ...data,
    });
    return data;
  }
};

export const getQuranJuzProgress = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const allJuz = Array.from({ length: 30 }, (_, i) => ({ juz_number: i + 1, completed: false }));

  const { data, error } = await supabase
    .from('quran_juz_progress')
    .select('*')
    .eq('user_id', user.id);

  if (error) throw error;

  const progressMap = (data || []).reduce((acc, item) => {
    acc[item.juz_number] = item;
    return acc;
  }, {});

  return allJuz.map(juz => progressMap[juz.juz_number] || juz);
};

export const updateQuranJuz = async (juzNumber, completed) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: existing } = await supabase
    .from('quran_juz_progress')
    .select('*')
    .eq('user_id', user.id)
    .eq('juz_number', juzNumber)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('quran_juz_progress')
      .update({ completed, completion_date: completed ? getLocalDateString() : null })
      .eq('id', existing.id);

    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('quran_juz_progress')
      .insert([{
        user_id: user.id,
        juz_number: juzNumber,
        completed,
        completion_date: completed ? getLocalDateString() : null
      }]);

    if (error) throw error;
  }
};

export const getQuranStats = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: juzData } = await supabase
    .from('quran_juz_progress')
    .select('completed')
    .eq('user_id', user.id)
    .eq('completed', true);

  const { data: surahData } = await supabase
    .from('quran_surah_progress')
    .select('reading_completed, memorization_status')
    .eq('user_id', user.id);

  const { data: dailyLogs } = await supabase
    .from('quran_daily_logs')
    .select('date')
    .eq('user_id', user.id)
    .order('date', { ascending: false });

  const juzCompleted = juzData?.length || 0;
  const surahsRead = surahData?.filter(s => s.reading_completed).length || 0;
  const surahsMemorized = surahData?.filter(s => s.memorization_status === 'completed').length || 0;
  const surahsMemorizing = surahData?.filter(s => s.memorization_status === 'in_progress').length || 0;

  let readingStreak = 0;
  if (dailyLogs && dailyLogs.length > 0) {
    const today = getLocalDateString();
    let currentDate = new Date(today);

    for (const log of dailyLogs) {
      const logDate = log.date;
      const checkDate = getLocalDateString(currentDate);

      if (logDate === checkDate) {
        readingStreak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else {
        break;
      }
    }
  }

  return {
    juz_completed: juzCompleted,
    completion_percentage: Math.round((juzCompleted / 30) * 100),
    surahs_read: surahsRead,
    surahs_memorized: surahsMemorized,
    surahs_memorizing: surahsMemorizing,
    reading_streak: readingStreak
  };
};

export const getBereketActivities = async () => {
  const { data, error } = await supabase
    .from('bereket_activities')
    .select('*, activity_id:id')
    .order('category', { ascending: true })
    .order('base_points', { ascending: true });

  if (error) throw error;
  return data;
};

export const getCustomActivities = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('custom_activities')
    .select('*, activity_id:id')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const getSadaqahEntries = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('sadaqah')
    .select('id, amount, description, date, created_at')
    .eq('user_id', user.id)
    .order('date', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const createSadaqahEntry = async ({ amount, description, date }) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const payload = {
    user_id: user.id,
    amount: Number(amount),
    description: String(description || '').trim(),
    date: date || getLocalDateString(),
  };

  const { data, error } = await supabase
    .from('sadaqah')
    .insert([payload])
    .select('id, amount, description, date, created_at')
    .single();

  if (error) throw error;
  return data;
};

export const getGoodDeedsEntries = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('good_deeds')
    .select('id, title, description, date, created_at')
    .eq('user_id', user.id)
    .order('date', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const createGoodDeedEntry = async ({ title, description, date }) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const payload = {
    user_id: user.id,
    title: String(title || '').trim(),
    description: String(description || '').trim(),
    date: date || getLocalDateString(),
  };

  const { data, error } = await supabase
    .from('good_deeds')
    .insert([payload])
    .select('id, title, description, date, created_at')
    .single();

  if (error) throw error;
  return data;
};

export const getTodayBereket = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const today = getLocalDateString();

  const { data, error } = await supabase
    .from('bereket_logs')
    .select('activity_id, points_earned')
    .eq('user_id', user.id)
    .eq('date', today);

  if (error) throw error;

  const activitiesLogged = {};
  let todayPoints = 0;

  (data || []).forEach(log => {
    activitiesLogged[log.activity_id] = (activitiesLogged[log.activity_id] || 0) + log.points_earned;
    todayPoints += log.points_earned;
  });

  let todayLevel = 'Starting the Journey';
  let todayLevelEmoji = '🌾';

  if (todayPoints >= 36) {
    todayLevel = 'Day of Spiritual Breakthrough';
    todayLevelEmoji = '✨';
  } else if (todayPoints >= 21) {
    todayLevel = 'Day of Barakah';
    todayLevelEmoji = '🌟';
  } else if (todayPoints >= 11) {
    todayLevel = 'Day of Growth';
    todayLevelEmoji = '🌱';
  } else if (todayPoints >= 5) {
    todayLevel = 'Day of Awareness';
    todayLevelEmoji = '🌿';
  }

  return {
    today_points: todayPoints,
    today_level: todayLevel,
    today_level_emoji: todayLevelEmoji,
    activities_logged: activitiesLogged
  };
};

export const getTodayBereketActivityLogs = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const today = getLocalDateString();
  const { data, error } = await supabase
    .from('bereket_logs')
    .select('id,activity_id,points_earned,is_secret,date,created_at,bereket_activities(name_en,name_hr)')
    .eq('user_id', user.id)
    .eq('date', today)
    .order('created_at', { ascending: false });

  if (error) throw error;

  let rows = data || [];

  // Fallback if relational join is empty for this tenant/schema.
  const activityNameById = new Map();
  rows.forEach((row) => {
    const nameEn = row?.bereket_activities?.name_en;
    const nameHr = row?.bereket_activities?.name_hr;
    if (row?.activity_id && (nameEn || nameHr)) {
      activityNameById.set(row.activity_id, { name_en: nameEn, name_hr: nameHr });
    }
  });

  if (activityNameById.size === 0 && rows.length > 0) {
    const ids = [...new Set(rows.map((row) => row.activity_id).filter(Boolean))];
    if (ids.length > 0) {
      const { data: activityMeta } = await supabase
        .from('bereket_activities')
        .select('id,name_en,name_hr')
        .in('id', ids);

      (activityMeta || []).forEach((row) => {
        if (row?.id) {
          activityNameById.set(row.id, { name_en: row.name_en, name_hr: row.name_hr });
        }
      });
    }

    rows = rows.map((row) => ({
      ...row,
      bereket_activities: activityNameById.get(row.activity_id) || { name_en: 'Activity', name_hr: 'Aktivnost' },
    }));
  }

  return rows;
};

export const deleteBereketActivityLog = async (logId) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('bereket_logs')
    .delete()
    .eq('id', logId)
    .eq('user_id', user.id);

  if (error) throw error;

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('bereket:activity-logged', {
      detail: { type: 'deleted', logId }
    }));
  }

  return { success: true };
};

export const logBereketActivity = async (activityId, pointsEarned, isSecret = false, activityName = '') => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const today = getLocalDateString();

  const { error } = await supabase
    .from('bereket_logs')
    .insert([{
      user_id: user.id,
      activity_id: activityId,
      date: today,
      points_earned: pointsEarned,
      is_secret: isSecret
    }]);

  if (error) throw error;

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('bereket:activity-logged', {
      detail: {
        activityId,
        pointsEarned,
        activityName,
        date: today,
      }
    }));
  }

  // Send notification for the activity (non-blocking)
  if (activityName && !isSecret) {
    sendActivityNotification(activityName, pointsEarned).catch(err => {
      console.log('Activity notification skipped:', err);
    });
  }
};

export const addCustomActivity = async (activity) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('custom_activities')
    .insert([{
      user_id: user.id,
      ...activity
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteCustomActivity = async (activityId) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('custom_activities')
    .delete()
    .eq('id', activityId)
    .eq('user_id', user.id);

  if (error) throw error;
};

export const getBereketStats = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const startDate = getLocalDateString(sevenDaysAgo);

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const monthStartDate = getLocalDateString(thirtyDaysAgo);

  const [weeklyRes, monthlyRes, allTimeRes] = await Promise.all([
    supabase
      .from('bereket_logs')
      .select('points_earned, date')
      .eq('user_id', user.id)
      .gte('date', startDate),
    supabase
      .from('bereket_logs')
      .select('points_earned, date')
      .eq('user_id', user.id)
      .gte('date', monthStartDate),
    supabase
      .from('bereket_logs')
      .select('points_earned, date')
      .eq('user_id', user.id)
  ]);

  const weeklyLogs = weeklyRes.data || [];
  const monthlyLogs = monthlyRes.data || [];
  const allTimeLogs = allTimeRes.data || [];

  const weeklyTotal = weeklyLogs.reduce((sum, log) => sum + log.points_earned, 0);
  const monthlyTotal = monthlyLogs.reduce((sum, log) => sum + log.points_earned, 0);
  const totalPoints = allTimeLogs.reduce((sum, log) => sum + log.points_earned, 0);

  const dailyBreakdown = {};
  weeklyLogs.forEach(log => {
    if (!dailyBreakdown[log.date]) {
      dailyBreakdown[log.date] = 0;
    }
    dailyBreakdown[log.date] += log.points_earned;
  });

  const last7Days = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = getLocalDateString(date);
    last7Days.push({
      date: dateStr,
      points: dailyBreakdown[dateStr] || 0
    });
  }

  let currentStreak = 0;
  let longestStreak = 0;
  let tempStreak = 0;

  const sortedDates = Object.keys(dailyBreakdown).sort();
  let yesterday = null;

  for (const date of sortedDates) {
    if (dailyBreakdown[date] > 0) {
      if (!yesterday) {
        tempStreak = 1;
      } else {
        const diff = (new Date(date) - new Date(yesterday)) / (1000 * 60 * 60 * 24);
        if (diff === 1) {
          tempStreak++;
        } else {
          longestStreak = Math.max(longestStreak, tempStreak);
          tempStreak = 1;
        }
      }
      yesterday = date;
    }
  }
  longestStreak = Math.max(longestStreak, tempStreak);

  const todayStr = getLocalDateString();
  if (yesterday === todayStr) {
    currentStreak = tempStreak;
  } else if (yesterday) {
    const diff = (new Date(todayStr) - new Date(yesterday)) / (1000 * 60 * 60 * 24);
    if (diff === 1) {
      currentStreak = tempStreak;
    }
  }

  const todayLogs2 = allTimeLogs.filter(l => l.date === todayStr);
  const todayTotal = todayLogs2.reduce((sum, log) => sum + log.points_earned, 0);

  let todayLevel = 'Starting the Journey';
  let todayLevelEmoji = '🌱';
  if (todayTotal >= 36) { todayLevel = 'Day of Spiritual Breakthrough'; todayLevelEmoji = '✨'; }
  else if (todayTotal >= 21) { todayLevel = 'Day of Barakah'; todayLevelEmoji = '🌳'; }
  else if (todayTotal >= 11) { todayLevel = 'Day of Growth'; todayLevelEmoji = '🌿'; }
  else if (todayTotal >= 5) { todayLevel = 'Day of Awareness'; todayLevelEmoji = '🌱'; }

  const categoryLogs = allTimeLogs.slice(0, 100);
  const activitiesByCategory = { basic: 0, medium: 0, advanced: 0 };

  return {
    total_points: totalPoints,
    weekly_total: weeklyTotal,
    monthly_total: monthlyTotal,
    week_points: weeklyTotal,
    month_points: monthlyTotal,
    today_points: todayTotal,
    today_level: todayLevel,
    today_level_emoji: todayLevelEmoji,
    activities_by_category: activitiesByCategory,
    current_streak: currentStreak,
    longest_streak: longestStreak,
    last_7_days: last7Days
  };
};

export const getBereketReflectionToday = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const today = getLocalDateString();

  const { data, error } = await supabase
    .from('bereket_reflections')
    .select('*')
    .eq('user_id', user.id)
    .eq('date', today)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') throw error;

  return data;
};

export const saveBereketReflection = async (reflection) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const today = getLocalDateString();

  const { data: existing } = await supabase
    .from('bereket_reflections')
    .select('id')
    .eq('user_id', user.id)
    .eq('date', today)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('bereket_reflections')
      .update(reflection)
      .eq('id', existing.id);

    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('bereket_reflections')
      .insert([{
        user_id: user.id,
        date: today,
        ...reflection
      }]);

    if (error) throw error;
  }
};

const getQuranProxyCandidateUrls = (path) => {
  const safePath = String(path || '').replace(/^\/+/, '');
  const encodedPath = encodeURIComponent(safePath);
  const envBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
  const candidates = [];

  if (typeof window !== 'undefined') {
    // Use a single primary candidate in production to avoid duplicate proxy load.
    candidates.push(`/api/quran/external?path=${encodedPath}`);

    if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
      if (envBackendUrl) {
        candidates.push(`${envBackendUrl}/api/quran/external?path=${encodedPath}`);
      }
      candidates.push(`http://127.0.0.1:8001/api/quran/external?path=${encodedPath}`);
    }
  } else if (envBackendUrl) {
    candidates.push(`${envBackendUrl}/api/quran/external?path=${encodedPath}`);
  }

  return [...new Set(candidates)];
};

const QURAN_COM_BASE = 'https://api.quran.com/api/v4';

const QURAN_COM_TRANSLATION_MAP = {
  'bs.korkut': 126,
  'bs.mlivo': 25,
  'en.sahih': 20,
  'en.pickthall': 19,
  'en.yusufali': 22,
  'en.hilali': 203,
  'tr.diyanet': 77,
  'tr.vakfi': 210,
  'tr.yazir': 52,
};

const QURAN_COM_ID_TO_IDENTIFIER = {
  126: 'bs.korkut',
  25: 'bs.mlivo',
  20: 'en.sahih',
  19: 'en.pickthall',
  22: 'en.yusufali',
  203: 'en.hilali',
  77: 'tr.diyanet',
  210: 'tr.vakfi',
  52: 'tr.yazir',
};

const EMERGENCY_FALLBACK_TRANSLATION_IDENTIFIER = 'bs.korkut';
const QURAN_VIEW_CACHE_PREFIX = 'quran_view_cache_v1:';

const EMERGENCY_FATIHA_AYAHS = [
  { number: 1, numberInSurah: 1, text: 'بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ', translation: 'U ime Allaha, Milostivog, Samilosnog!' },
  { number: 2, numberInSurah: 2, text: 'ٱلْحَمْدُ لِلَّٰهِ رَبِّ ٱلْعَٰلَمِينَ', translation: 'Hvala Allahu, Gospodaru svjetova,' },
  { number: 3, numberInSurah: 3, text: 'ٱلرَّحْمَٰنِ ٱلرَّحِيمِ', translation: 'Milostivom, Samilosnom,' },
  { number: 4, numberInSurah: 4, text: 'مَٰلِكِ يَوْمِ ٱلدِّينِ', translation: 'Vladaru Dana sudnjeg,' },
  { number: 5, numberInSurah: 5, text: 'إِيَّاكَ نَعْبُدُ وَإِيَّاكَ نَسْتَعِينُ', translation: 'Samo Tebi robujemo i samo od Tebe pomoć tražimo!' },
  { number: 6, numberInSurah: 6, text: 'ٱهْدِنَا ٱلصِّرَٰطَ ٱلْمُسْتَقِيمَ', translation: 'Uputi nas na Pravi put,' },
  { number: 7, numberInSurah: 7, text: 'صِرَٰطَ ٱلَّذِينَ أَنْعَمْتَ عَلَيْهِمْ غَيْرِ ٱلْمَغْضُوبِ عَلَيْهِمْ وَلَا ٱلضَّالِّينَ', translation: 'na put onih kojima si milost Svoju darovao, a ne onih koji su protiv sebe srdžbu izazvali, niti onih koji su zalutali!' },
];

const EMERGENCY_SURAH_FALLBACK_NAMES = {
  1: { name: 'ٱلْفَاتِحَة', englishName: 'Al-Fatihah', englishNameTranslation: 'The Opening' },
  112: { name: 'ٱلْإِخْلَاص', englishName: 'Al-Ikhlas', englishNameTranslation: 'Sincerity' },
  113: { name: 'ٱلْفَلَق', englishName: 'Al-Falaq', englishNameTranslation: 'Daybreak' },
  114: { name: 'ٱلنَّاس', englishName: 'An-Nas', englishNameTranslation: 'Mankind' },
};

const getQuranViewCache = (key) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(`${QURAN_VIEW_CACHE_PREFIX}${key}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setQuranViewCache = (key, value) => {
  if (typeof window === 'undefined' || !value) return;
  try {
    localStorage.setItem(`${QURAN_VIEW_CACHE_PREFIX}${key}`, JSON.stringify(value));
  } catch {
    // no-op
  }
};

const withDegradedMeta = (payload, source, message) => {
  if (!payload || typeof payload !== 'object') return payload;
  return {
    ...payload,
    degraded: true,
    degraded_source: source,
    degraded_message: message,
    degraded_translation_identifier: EMERGENCY_FALLBACK_TRANSLATION_IDENTIFIER,
  };
};

const getEmergencySurahMeta = (surahNumber = 1) => {
  const safeSurah = Number.isFinite(Number(surahNumber)) ? Number(surahNumber) : 1;
  const known = EMERGENCY_SURAH_FALLBACK_NAMES[safeSurah] || {
    name: '—',
    englishName: `Surah ${safeSurah}`,
    englishNameTranslation: 'Emergency Fallback',
  };
  return {
    number: safeSurah,
    name: known.name,
    englishName: known.englishName,
    englishNameTranslation: known.englishNameTranslation,
    numberOfAyahs: 7,
    revelationType: 'meccan',
  };
};

const getEmergencyAyahSeed = (requestedAyah = 1) => {
  const index = Number.isFinite(Number(requestedAyah)) ? Number(requestedAyah) - 1 : 0;
  return EMERGENCY_FATIHA_AYAHS[Math.abs(index) % EMERGENCY_FATIHA_AYAHS.length];
};

const buildEmergencyAyahFallback = (surahNumber = 1, ayahNumber = 1, message = 'Emergency local Quran fallback') => {
  const surahMeta = getEmergencySurahMeta(surahNumber);
  const seed = getEmergencyAyahSeed(ayahNumber);

  return withDegradedMeta({
    number: seed.number,
    surah: {
      number: surahMeta.number,
      name: surahMeta.name,
      englishName: surahMeta.englishName,
      englishNameTranslation: surahMeta.englishNameTranslation,
      numberOfAyahs: surahMeta.numberOfAyahs,
    },
    numberInSurah: Number.isFinite(Number(ayahNumber)) ? Number(ayahNumber) : 1,
    text: seed.text,
    translation: seed.translation,
    audio: `https://cdn.islamic.network/quran/audio/128/ar.alafasy/${seed.number}.mp3`,
    page: 1,
    juz: 1,
  }, 'local', message);
};

const buildEmergencySurahFallback = (surahNumber = 1, message = 'Emergency local Quran fallback') => {
  const surahMeta = getEmergencySurahMeta(surahNumber);
  const ayahs = EMERGENCY_FATIHA_AYAHS.map((ayah) => ({
    number: ayah.number,
    numberInSurah: ayah.numberInSurah,
    text: ayah.text,
    translation: ayah.translation,
    audio: `https://cdn.islamic.network/quran/audio/128/ar.alafasy/${ayah.number}.mp3`,
    page: 1,
    juz: 1,
  }));

  return withDegradedMeta({
    ...surahMeta,
    ayahs,
  }, 'local', message);
};

const buildEmergencyPageFallback = (pageNumber = 1, message = 'Emergency local Quran fallback') => {
  const surahMeta = getEmergencySurahMeta(1);
  const ayahs = EMERGENCY_FATIHA_AYAHS.map((ayah) => ({
    number: ayah.number,
    numberInSurah: ayah.numberInSurah,
    text: ayah.text,
    translation: ayah.translation,
    surah: {
      number: surahMeta.number,
      name: surahMeta.name,
      englishName: surahMeta.englishName,
      englishNameTranslation: surahMeta.englishNameTranslation,
      numberOfAyahs: surahMeta.numberOfAyahs,
    },
    page: Number.isFinite(Number(pageNumber)) ? Number(pageNumber) : 1,
    juz: 1,
  }));

  return withDegradedMeta({
    number: Number.isFinite(Number(pageNumber)) ? Number(pageNumber) : 1,
    ayahs,
    surahs: { 1: surahMeta },
  }, 'local', message);
};

const buildEmergencyJuzFallback = (juzNumber = 1, message = 'Emergency local Quran fallback') => {
  const surahMeta = getEmergencySurahMeta(1);
  const ayahs = EMERGENCY_FATIHA_AYAHS.map((ayah) => ({
    number: ayah.number,
    numberInSurah: ayah.numberInSurah,
    text: ayah.text,
    translation: ayah.translation,
    surah: {
      number: surahMeta.number,
      name: surahMeta.name,
      englishName: surahMeta.englishName,
      englishNameTranslation: surahMeta.englishNameTranslation,
      numberOfAyahs: surahMeta.numberOfAyahs,
    },
    page: 1,
    juz: Number.isFinite(Number(juzNumber)) ? Number(juzNumber) : 1,
  }));

  return withDegradedMeta({
    number: Number.isFinite(Number(juzNumber)) ? Number(juzNumber) : 1,
    ayahs,
    surahs: { 1: surahMeta },
  }, 'local', message);
};

const buildEmergencySurahListFallback = () => {
  return Array.from({ length: 114 }, (_, idx) => {
    const number = idx + 1;
    const known = EMERGENCY_SURAH_FALLBACK_NAMES[number] || {};
    return {
      number,
      name: known.name || '—',
      englishName: known.englishName || `Surah ${number}`,
      englishNameTranslation: known.englishNameTranslation || 'Emergency Fallback',
      numberOfAyahs: number === 1 ? 7 : 0,
      revelationType: 'unknown',
    };
  });
};

const normalizeHtmlText = (text = '') => String(text || '').replace(/<[^>]*>/g, '').trim();

const getQuranComTranslationId = (identifier = 'en.sahih') => {
  const raw = String(identifier || 'en.sahih');
  if (raw.startsWith('qcom.')) {
    const parsed = parseInt(raw.replace('qcom.', ''), 10);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return QURAN_COM_TRANSLATION_MAP[raw] || 20;
};

const getSearchLanguage = (identifier = 'en.sahih') => {
  if (identifier === 'quran-simple') return 'arabic';
  const code = String(identifier).split('.')[0]?.toLowerCase();
  if (code === 'bs') return 'bosnian';
  if (code === 'tr') return 'turkish';
  if (code === 'sq') return 'albanian';
  return 'english';
};

let quranComChaptersCache = null;

const fetchQuranComChaptersMap = async () => {
  if (quranComChaptersCache) return quranComChaptersCache;

  const response = await fetch(`${QURAN_COM_BASE}/chapters?language=en`);
  if (!response.ok) {
    throw new Error(`Quran.com chapters failed (${response.status})`);
  }

  const payload = await response.json();
  const chapters = payload?.chapters || [];
  const map = {};

  chapters.forEach((chapter) => {
    map[chapter.id] = {
      number: chapter.id,
      name: chapter.name_arabic,
      englishName: chapter.name_simple,
      englishNameTranslation: chapter.translated_name?.name,
      numberOfAyahs: chapter.verses_count,
      revelationType: chapter.revelation_place,
    };
  });

  quranComChaptersCache = map;
  return map;
};

const parseVerseKey = (verseKey = '') => {
  const [surahRaw, ayahRaw] = String(verseKey).split(':');
  return {
    surahNumber: parseInt(surahRaw, 10) || 1,
    ayahNumber: parseInt(ayahRaw, 10) || 1,
  };
};

const mapQuranComVerses = (verses = [], chaptersMap = {}, isArabic = false) => {
  return verses.map((verse) => {
    const { surahNumber, ayahNumber } = parseVerseKey(verse.verse_key);
    const chapterMeta = chaptersMap[surahNumber] || {};
    const translationText = normalizeHtmlText(verse?.translations?.[0]?.text || '');

    return {
      number: verse.id,
      numberInSurah: ayahNumber || verse.verse_number,
      text: isArabic ? normalizeHtmlText(verse.text_uthmani || verse.text_indopak || '') : translationText,
      translation: isArabic ? '' : translationText,
      audio: `https://cdn.islamic.network/quran/audio/128/ar.alafasy/${verse.id}.mp3`,
      page: verse.page_number,
      juz: verse.juz_number,
      surah: {
        number: surahNumber,
        name: chapterMeta.name,
        englishName: chapterMeta.englishName,
        englishNameTranslation: chapterMeta.englishNameTranslation,
        numberOfAyahs: chapterMeta.numberOfAyahs,
      },
    };
  });
};

const fetchQuranFromQuranCom = async (path) => {
  const cleanPath = String(path || '').replace(/^\/+/, '');

  if (cleanPath === 'surah') {
    const chaptersMap = await fetchQuranComChaptersMap();
    return {
      code: 200,
      status: 'OK',
      data: Object.values(chaptersMap),
    };
  }

  if (cleanPath === 'edition?format=text&type=translation') {
    const response = await fetch(`${QURAN_COM_BASE}/resources/translations`);
    if (!response.ok) throw new Error(`Quran.com translations failed (${response.status})`);
    const payload = await response.json();
    const translations = (payload?.translations || []).map((item) => {
      const identifier = QURAN_COM_ID_TO_IDENTIFIER[item.id] || `qcom.${item.id}`;
      return {
        identifier,
        language: item.language_name,
        englishName: item.name,
        name: item.name,
      };
    });
    return { code: 200, status: 'OK', data: translations };
  }

  const ayahMatch = cleanPath.match(/^ayah\/(\d+):(\d+)\/(.+)$/);
  if (ayahMatch) {
    const [, surahRaw, ayahRaw, edition] = ayahMatch;
    const surahNumber = parseInt(surahRaw, 10);
    const ayahNumber = parseInt(ayahRaw, 10);
    const isArabic = String(edition).startsWith('quran') || String(edition).startsWith('ar.');
    const translationId = getQuranComTranslationId(edition);

    const params = new URLSearchParams({ words: 'false', fields: 'text_uthmani' });
    if (!isArabic) params.set('translations', String(translationId));
    const response = await fetch(`${QURAN_COM_BASE}/verses/by_key/${surahNumber}:${ayahNumber}?${params.toString()}`);
    if (!response.ok) throw new Error(`Quran.com ayah failed (${response.status})`);
    const payload = await response.json();

    const chaptersMap = await fetchQuranComChaptersMap();
    const mapped = mapQuranComVerses([payload.verse], chaptersMap, isArabic)[0];

    return {
      code: 200,
      status: 'OK',
      data: {
        number: mapped.number,
        surah: mapped.surah,
        numberInSurah: mapped.numberInSurah,
        text: mapped.text,
        audio: mapped.audio,
        page: mapped.page,
        juz: mapped.juz,
      },
    };
  }

  const surahMatch = cleanPath.match(/^surah\/(\d+)\/(.+)$/);
  if (surahMatch) {
    const [, surahRaw, edition] = surahMatch;
    const surahNumber = parseInt(surahRaw, 10);
    const isArabic = String(edition).startsWith('quran') || String(edition).startsWith('ar.');
    const translationId = getQuranComTranslationId(edition);

    const params = new URLSearchParams({ words: 'false', per_page: '300', fields: 'text_uthmani' });
    if (!isArabic) params.set('translations', String(translationId));
    const response = await fetch(`${QURAN_COM_BASE}/verses/by_chapter/${surahNumber}?${params.toString()}`);
    if (!response.ok) throw new Error(`Quran.com surah failed (${response.status})`);
    const payload = await response.json();

    const chaptersMap = await fetchQuranComChaptersMap();
    const mappedAyahs = mapQuranComVerses(payload.verses || [], chaptersMap, isArabic);
    const chapterMeta = chaptersMap[surahNumber] || {};

    return {
      code: 200,
      status: 'OK',
      data: {
        ...chapterMeta,
        ayahs: mappedAyahs.map((ayah) => ({
          number: ayah.number,
          numberInSurah: ayah.numberInSurah,
          text: ayah.text,
          audio: ayah.audio,
          page: ayah.page,
          juz: ayah.juz,
        })),
      },
    };
  }

  const pageMatch = cleanPath.match(/^page\/(\d+)\/(.+)$/);
  if (pageMatch) {
    const [, pageRaw, edition] = pageMatch;
    const pageNumber = parseInt(pageRaw, 10);
    const isArabic = String(edition).startsWith('quran') || String(edition).startsWith('ar.');
    const translationId = getQuranComTranslationId(edition);
    const params = new URLSearchParams({ words: 'false', per_page: '300', fields: 'text_uthmani' });
    if (!isArabic) params.set('translations', String(translationId));
    const response = await fetch(`${QURAN_COM_BASE}/verses/by_page/${pageNumber}?${params.toString()}`);
    if (!response.ok) throw new Error(`Quran.com page failed (${response.status})`);
    const payload = await response.json();

    const chaptersMap = await fetchQuranComChaptersMap();
    const mappedAyahs = mapQuranComVerses(payload.verses || [], chaptersMap, isArabic);

    return {
      code: 200,
      status: 'OK',
      data: {
        ayahs: mappedAyahs.map((ayah) => ({
          number: ayah.number,
          numberInSurah: ayah.numberInSurah,
          text: ayah.text,
          audio: ayah.audio,
          page: ayah.page,
          juz: ayah.juz,
          surah: ayah.surah,
        })),
        surahs: chaptersMap,
      },
    };
  }

  const juzMatch = cleanPath.match(/^juz\/(\d+)\/(.+)$/);
  if (juzMatch) {
    const [, juzRaw, edition] = juzMatch;
    const juzNumber = parseInt(juzRaw, 10);
    const isArabic = String(edition).startsWith('quran') || String(edition).startsWith('ar.');
    const translationId = getQuranComTranslationId(edition);
    const params = new URLSearchParams({ words: 'false', per_page: '300', fields: 'text_uthmani' });
    if (!isArabic) params.set('translations', String(translationId));
    const response = await fetch(`${QURAN_COM_BASE}/verses/by_juz/${juzNumber}?${params.toString()}`);
    if (!response.ok) throw new Error(`Quran.com juz failed (${response.status})`);
    const payload = await response.json();

    const chaptersMap = await fetchQuranComChaptersMap();
    const mappedAyahs = mapQuranComVerses(payload.verses || [], chaptersMap, isArabic);

    return {
      code: 200,
      status: 'OK',
      data: {
        ayahs: mappedAyahs.map((ayah) => ({
          number: ayah.number,
          numberInSurah: ayah.numberInSurah,
          text: ayah.text,
          audio: ayah.audio,
          page: ayah.page,
          juz: ayah.juz,
          surah: ayah.surah,
        })),
        surahs: chaptersMap,
      },
    };
  }

  const searchMatch = cleanPath.match(/^search\/(.+)\/all\/(.+)$/);
  if (searchMatch) {
    const [, encodedKeyword, translationIdentifier] = searchMatch;
    const keyword = decodeURIComponent(encodedKeyword);
    const language = getSearchLanguage(translationIdentifier);

    const params = new URLSearchParams({
      q: keyword,
      size: '40',
      page: '1',
      language,
    });

    const response = await fetch(`${QURAN_COM_BASE}/search?${params.toString()}`);
    if (!response.ok) throw new Error(`Quran.com search failed (${response.status})`);
    const payload = await response.json();
    const chaptersMap = await fetchQuranComChaptersMap();

    const results = (payload?.search?.results || []).map((item) => {
      const { surahNumber, ayahNumber } = parseVerseKey(item.verse_key);
      const chapterMeta = chaptersMap[surahNumber] || {};
      return {
        number: item.verse_id,
        numberInSurah: ayahNumber,
        text: normalizeHtmlText(item.text),
        surah: {
          number: surahNumber,
          name: chapterMeta.name,
          englishName: chapterMeta.englishName,
        },
      };
    });

    return {
      code: 200,
      status: 'OK',
      data: {
        count: results.length,
        matches: results,
      },
    };
  }

  throw new Error('Unsupported Quran path');
};

const fetchQuranExternal = async (path) => {
  try {
    return await fetchQuranFromQuranCom(path);
  } catch {
    // Fallback to existing backend proxy path if Quran.com request fails.
  }

  const candidateUrls = getQuranProxyCandidateUrls(path);
  let lastError = `Failed to fetch Quran path: ${path}`;
  const cacheKey = `quran_external_cache_v1:${path}`;

  for (const url of candidateUrls) {
    for (let attempt = 1; attempt <= 2; attempt += 1) {
      try {
        const response = await fetch(url);

        if (!response.ok) {
          try {
            const parsedError = await response.json();
            lastError = parsedError?.detail || parsedError?.message || `Quran proxy request failed (${response.status})`;
          } catch {
            lastError = `Quran proxy request failed (${response.status})`;
          }

          if (attempt < 2) {
            await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
            continue;
          }
          break;
        }

          try {
          const payload = await response.json();
          if (typeof window !== 'undefined') {
            try {
              localStorage.setItem(cacheKey, JSON.stringify(payload));
            } catch (storageError) {
              console.warn('Quran external cache write failed:', storageError);
            }
          }
          return payload;
        } catch {
          lastError = `Quran proxy returned non-JSON response for ${path}`;
          break;
        }
      } catch (error) {
        lastError = error?.message || `Failed to fetch Quran path: ${path}`;
        if (attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 350 * attempt));
          continue;
        }
      }
    }
  }

  if (typeof window !== 'undefined') {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (cacheReadError) {
      console.warn('Quran external cache read failed:', cacheReadError);
    }
  }

  throw new Error(lastError);
};

export const getQuranSurahs = async () => {
  const cacheKey = 'surah-list';
  try {
    const result = await fetchQuranExternal('surah');

    if (result.code === 200 && result.data) {
      const normalized = result.data.map(surah => ({
        number: surah.number,
        name: surah.name,
        englishName: surah.englishName,
        englishNameTranslation: surah.englishNameTranslation,
        numberOfAyahs: surah.numberOfAyahs,
        revelationType: surah.revelationType
      }));
      setQuranViewCache(cacheKey, normalized);
      return normalized;
    }

    throw new Error('Failed to fetch surahs');
  } catch (error) {
    const message = error?.message || 'Quran surahs API unavailable';
    const cached = getQuranViewCache(cacheKey);
    if (Array.isArray(cached) && cached.length > 0) {
      return cached;
    }

    console.error('Quran surahs API error, using emergency fallback:', message);
    return buildEmergencySurahListFallback();
  }
};

export const getQuranReciters = () => {
  return [
    { identifier: 'ar.alafasy', name: 'Mishary Rashid Alafasy', language: 'ar', style: 'Murattal' },
    { identifier: 'ar.abdulbasitmurattal', name: 'Abdul Basit (Murattal)', language: 'ar', style: 'Murattal' },
    { identifier: 'ar.abdurrahmaansudais', name: 'Abdurrahmaan As-Sudais', language: 'ar', style: 'Murattal' },
    { identifier: 'ar.shaatree', name: 'Abu Bakr Ash-Shaatree', language: 'ar', style: 'Murattal' },
    { identifier: 'ar.husary', name: 'Mahmoud Khalil Al-Husary', language: 'ar', style: 'Murattal' },
    { identifier: 'ar.minshawi', name: 'Mohamed Siddiq Al-Minshawi', language: 'ar', style: 'Mujawwad' },
    { identifier: 'ar.muhammadayyoub', name: 'Muhammad Ayyoub', language: 'ar', style: 'Murattal' },
    { identifier: 'ar.muhammadjibreel', name: 'Muhammad Jibreel', language: 'ar', style: 'Murattal' },
    { identifier: 'ar.azeezalili', name: 'Aziz Alili', language: 'ar', style: 'Murattal' }
  ];
};

const STATIC_QURAN_TRANSLATIONS = [
  { identifier: 'bs.korkut', name: 'Besim Korkut', language: 'Bosnian' },
  { identifier: 'bs.mlivo', name: 'Muhamed Mlivo', language: 'Bosnian' },
  { identifier: 'en.sahih', name: 'Sahih International', language: 'English' },
  { identifier: 'en.pickthall', name: 'Pickthall', language: 'English' },
  { identifier: 'en.yusufali', name: 'Yusuf Ali', language: 'English' },
  { identifier: 'en.hilali', name: 'Hilali & Khan', language: 'English' },
  { identifier: 'tr.diyanet', name: 'Diyanet İşleri', language: 'Turkish' },
  { identifier: 'tr.vakfi', name: 'Diyanet Vakfı', language: 'Turkish' },
  { identifier: 'tr.yazir', name: 'Elmalılı Hamdi Yazır', language: 'Turkish' },
];

const QURAN_TRANSLATIONS_STORAGE_KEY = 'quran_translations_catalog_v2';

const languageNames = {
  sq: 'Albanian',
  en: 'English',
  tr: 'Turkish',
  bs: 'Bosnian',
};

const normalizeLanguageName = (langCode = '') => {
  if (!langCode) return 'Unknown';
  const lower = String(langCode).toLowerCase();
  if (languageNames[lower]) return languageNames[lower];
  if (lower.length <= 3) return lower.toUpperCase();
  return lower
    .split(/[-_\s]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const sortQuranTranslations = (items) => {
  const getLanguageGroup = (item) => {
    const code = (item.identifier || '').split('.')[0]?.toLowerCase();

    if (item.identifier === 'bs.korkut') return 0;
    if (code === 'sq' || String(item.language).toLowerCase() === 'albanian') return 1;
    if (code === 'en' || String(item.language).toLowerCase() === 'english') return 2;
    if (code === 'tr' || String(item.language).toLowerCase() === 'turkish') return 3;
    return 4;
  };

  return [...items].sort((a, b) => {
    const groupDiff = getLanguageGroup(a) - getLanguageGroup(b);
    if (groupDiff !== 0) return groupDiff;

    const langA = String(a.language || '').toLowerCase();
    const langB = String(b.language || '').toLowerCase();
    if (langA !== langB) return langA.localeCompare(langB);

    const nameA = String(a.name || '').toLowerCase();
    const nameB = String(b.name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

const normalizeTranslationList = (entries) => {
  const uniq = new Map();

  entries.forEach((entry) => {
    const identifier = entry?.identifier;
    if (!identifier || uniq.has(identifier)) return;

    const languageCode = (identifier || '').split('.')[0]?.toLowerCase();
    uniq.set(identifier, {
      identifier,
      name: entry.englishName || entry.name || identifier,
      language: entry.language ? normalizeLanguageName(entry.language) : normalizeLanguageName(languageCode),
    });
  });

  return sortQuranTranslations(Array.from(uniq.values()));
};

let quranTranslationsCatalog = STATIC_QURAN_TRANSLATIONS;

if (typeof window !== 'undefined') {
  try {
    const raw = localStorage.getItem(QURAN_TRANSLATIONS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        quranTranslationsCatalog = normalizeTranslationList(parsed);
      }
    }
  } catch (error) {
    console.error('Error restoring Quran translations cache:', error);
  }
}

export const refreshQuranTranslations = async () => {
  try {
    const result = await fetchQuranExternal('edition?format=text&type=translation');
    const apiItems = Array.isArray(result?.data) ? result.data : [];

    const merged = normalizeTranslationList([...STATIC_QURAN_TRANSLATIONS, ...apiItems]);
    if (merged.length > 0) {
      quranTranslationsCatalog = merged;

      if (typeof window !== 'undefined') {
        localStorage.setItem(QURAN_TRANSLATIONS_STORAGE_KEY, JSON.stringify(merged));
      }
    }

    return quranTranslationsCatalog;
  } catch (error) {
    console.error('Error fetching all Quran translations:', error);
    return quranTranslationsCatalog;
  }
};

export const getQuranTranslations = () => quranTranslationsCatalog;

export const getArabicFontStyles = () => {
  return [
    { value: 'uthmani', label: 'Uthmani (Traditional)', description: 'Classic Madani Mushaf style - KFGQPC Hafs' },
    { value: 'simple', label: 'Simple', description: 'Clean Arabic without complex orthography' },
    { value: 'indopak', label: 'IndoPak', description: 'South Asian style' },
    { value: 'naskh', label: 'Naskh', description: 'Modern Naskh script' }
  ];
};

export const getArabicEditionForStyle = (fontStyle) => {
  switch (fontStyle) {
    case 'simple': return 'quran-simple';
    default: return 'quran-uthmani';
  }
};

export const getQuranAyahWithTranslation = async (surahNumber, ayahNumber) => {
  try {
    const [arabic, translation] = await Promise.all([
      fetchQuranExternal(`ayah/${surahNumber}:${ayahNumber}/ar.alafasy`),
      fetchQuranExternal(`ayah/${surahNumber}:${ayahNumber}/en.sahih`),
    ]);

    if (arabic.code === 200 && translation.code === 200) {
      return {
        number: arabic.data.number,
        surah: {
          number: arabic.data.surah.number,
          name: arabic.data.surah.name,
          englishName: arabic.data.surah.englishName,
          englishNameTranslation: arabic.data.surah.englishNameTranslation,
          numberOfAyahs: arabic.data.surah.numberOfAyahs
        },
        numberInSurah: arabic.data.numberInSurah,
        text: arabic.data.text,
        translation: translation.data.text,
        audio: arabic.data.audio
      };
    }

    throw new Error('Failed to fetch ayah');
  } catch (error) {
    console.error('Quran ayah API error:', error);
    throw error;
  }
};

export const getQuranSurahWithTranslation = async (surahNumber) => {
  try {
    const [arabic, translation] = await Promise.all([
      fetchQuranExternal(`surah/${surahNumber}/ar.alafasy`),
      fetchQuranExternal(`surah/${surahNumber}/en.sahih`),
    ]);

    if (arabic.code === 200 && translation.code === 200) {
      const ayahs = arabic.data.ayahs.map((ayah, index) => ({
        number: ayah.number,
        numberInSurah: ayah.numberInSurah,
        text: ayah.text,
        translation: translation.data.ayahs[index]?.text || '',
        audio: ayah.audio
      }));

      return {
        number: arabic.data.number,
        name: arabic.data.name,
        englishName: arabic.data.englishName,
        englishNameTranslation: arabic.data.englishNameTranslation,
        numberOfAyahs: arabic.data.numberOfAyahs,
        revelationType: arabic.data.revelationType,
        ayahs
      };
    }

    throw new Error('Failed to fetch surah');
  } catch (error) {
    console.error('Quran surah API error:', error);
    throw error;
  }
};

export const getQuranMultipleAyahs = async (surahNumber, fromAyah, toAyah) => {
  try {
    const ayahs = [];

    for (let i = fromAyah; i <= toAyah; i++) {
      const ayah = await getQuranAyahWithTranslation(surahNumber, i);
      ayahs.push(ayah);
    }

    return ayahs;
  } catch (error) {
    console.error('Quran multiple ayahs API error:', error);
    throw error;
  }
};

export const getQuranUserPreferences = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('quran_user_preferences')
    .select('*')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') throw error;

  return data || {
    preferred_reciter: 'ar.alafasy',
    show_translation: true,
    show_arabic: true,
    reading_mode: 'ayah',
    ayahs_per_view: 1,
    last_surah: 1,
    last_ayah: 1,
    arabic_font_style: 'uthmani'
  };
};

export const updateQuranUserPreferences = async (preferences) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: existing } = await supabase
    .from('quran_user_preferences')
    .select('id')
    .eq('user_id', user.id)
    .maybeSingle();

  if (existing) {
    const { data, error } = await supabase
      .from('quran_user_preferences')
      .update(preferences)
      .eq('id', existing.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase
      .from('quran_user_preferences')
      .insert([{ user_id: user.id, ...preferences }])
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

export const getPlannerEvents = async (startDate, endDate) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const normalizedStartDate = normalizePlannerDateTimeValue(startDate) || startDate;
  const normalizedEndDate = normalizePlannerDateTimeValue(endDate) || endDate;

  const { data, error } = await supabase
    .from('planner_events')
    .select('*')
    .eq('user_id', user.id)
    .gte('start_time', normalizedStartDate)
    .lte('start_time', normalizedEndDate)
    .order('start_time', { ascending: true });

  if (error) throw error;
  return (data || []).map(normalizePlannerEventRecord);
};

export const createPlannerEvent = async (event) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Clean: remove undefined/null values and validate UUID fields
  const cleanEvent = {};
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  
  for (const [key, value] of Object.entries(event)) {
    if (value === undefined || value === null || value === '') continue;
    if (key === 'bereket_activity_id' && !uuidRegex.test(value)) continue;
    cleanEvent[key] = ['start_time', 'end_time'].includes(key)
      ? (normalizePlannerDateTimeValue(value) || value)
      : value;
  }

  // Use raw fetch to avoid supabase-js body stream issues
  const session = (await supabase.auth.getSession()).data.session;
  
  const response = await fetch(`${supabaseUrl}/rest/v1/planner_events`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': supabaseAnonKey,
      'Authorization': `Bearer ${session?.access_token || supabaseAnonKey}`,
      'Prefer': 'return=representation',
    },
    body: JSON.stringify({ user_id: user.id, ...cleanEvent }),
  });

  if (!response.ok) {
    // Clone to avoid "body stream already read" when interceptors consume the body
    let errText = '';
    try { errText = await response.clone().text(); } catch { errText = `HTTP ${response.status}`; }
    throw new Error(`Failed to create event: ${response.status} ${errText}`);
  }

  let data;
  try { data = await response.clone().json(); } catch { data = []; }
  return Array.isArray(data) ? data[0] : data;
};

export const updatePlannerEvent = async (eventId, updates) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Clean: remove null/undefined UUID fields
  const cleanUpdates = {};
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  
  for (const [key, value] of Object.entries(updates)) {
    if (key === 'bereket_activity_id' && (value === null || value === '' || (value && !uuidRegex.test(value)))) continue;
    if (value === undefined) continue;
    if (['start_time', 'end_time'].includes(key)) {
      cleanUpdates[key] = value === '' ? null : (normalizePlannerDateTimeValue(value) || value);
      continue;
    }
    cleanUpdates[key] = value;
  }

  const { data, error } = await supabase
    .from('planner_events')
    .update(cleanUpdates)
    .eq('id', eventId)
    .eq('user_id', user.id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deletePlannerEvent = async (eventId) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('planner_events')
    .delete()
    .eq('id', eventId)
    .eq('user_id', user.id);

  if (error) throw error;
};

export const getJournalEntries = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('journal_entries')
    .select('*')
    .eq('user_id', user.id)
    .order('date', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const getJournalEntryByDate = async (date) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('journal_entries')
    .select('*')
    .eq('user_id', user.id)
    .eq('date', date)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
};

export const saveJournalEntry = async (entry, date) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: existing } = await supabase
    .from('journal_entries')
    .select('id')
    .eq('user_id', user.id)
    .eq('date', date)
    .maybeSingle();

  if (existing) {
    const { data, error } = await supabase
      .from('journal_entries')
      .update(entry)
      .eq('id', existing.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase
      .from('journal_entries')
      .insert([{
        user_id: user.id,
        date,
        ...entry
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

export const deleteJournalEntry = async (entryId) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('journal_entries')
    .delete()
    .eq('id', entryId)
    .eq('user_id', user.id);

  if (error) throw error;
};

const getDefaultBarakahProfile = () => ({
  wake_window_start: '05:30',
  wake_window_end: '07:00',
  sleep_window_start: '22:00',
  sleep_window_end: '23:30',
  work_window_start: '09:00',
  work_window_end: '17:00',
  focus_areas: ['Salah', 'Quran'],
  habit_preference: 'small',
  notification_start: '07:00',
  notification_end: '21:00',
  city: 'Sarajevo',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Sarajevo',
  onboarding_skipped: false,
});

const getDefaultTodayPlan = (date = getLocalDateString()) => createDefaultTodayPlan(date);

const normalizePriorityItems = (priorities = []) => {
  const fromInput = Array.isArray(priorities) ? priorities : [];
  const inputItems = fromInput.length > 0 ? fromInput : [createEmptyTodayPlanPriority(0)];

  return inputItems.map((item = {}, idx) => {
    const fallback = createEmptyTodayPlanPriority(idx);
    let startTime = normalizeClockValue(item.start_time || '');
    let endTime = normalizeClockValue(item.end_time || '');

    if (!endTime && item.duration_minutes && startTime) {
      const dur = parseInt(item.duration_minutes, 10);
      if (Number.isFinite(dur) && dur > 0) {
        const [h, m] = String(startTime).split(':').map(Number);
        const totalMin = (h || 0) * 60 + (m || 0) + dur;
        endTime = `${String(Math.floor(totalMin / 60) % 24).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}`;
      }
    }

    return {
      id: item.id || fallback.id,
      title: String(item.title || ''),
      goal_niyyah: String(item.goal_niyyah || item.goal_intention || ''),
      start_time: startTime,
      end_time: endTime,
      reminder_minutes: Number.isFinite(Number(item.reminder_minutes)) ? Number(item.reminder_minutes) : null,
      blocked_event_id: item.blocked_event_id || null,
      blocked_start_time: item.blocked_start_time || null,
    };
  });
};

const normalizeTodayPlanShape = (raw, date) => {
  const fallback = getDefaultTodayPlan(date);
  const value = raw || {};
  return {
    date: value.date || fallback.date,
    niyyah: String(value.niyyah || ''),
    priorities: normalizePriorityItems(value.priorities),
    avoid_distraction: String(value.avoid_distraction || ''),
    notes: String(value.notes || ''),
    plan_items: Array.isArray(value.plan_items) ? value.plan_items : [],
  };
};

export const getBarakahProfile = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const fallbackKey = `barakah_profile_v1:${user.id}`;

  try {
    const { data, error } = await supabase
      .from('barakah_profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') throw error;
    if (!data) {
      return getDefaultBarakahProfile();
    }

    return {
      ...getDefaultBarakahProfile(),
      ...data,
      focus_areas: Array.isArray(data.focus_areas) ? data.focus_areas : getDefaultBarakahProfile().focus_areas,
    };
  } catch (error) {
    try {
      const raw = localStorage.getItem(fallbackKey);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object') {
        return {
          ...getDefaultBarakahProfile(),
          ...parsed,
          focus_areas: Array.isArray(parsed.focus_areas) ? parsed.focus_areas : getDefaultBarakahProfile().focus_areas,
        };
      }
    } catch {
      // no-op
    }
    return getDefaultBarakahProfile();
  }
};

export const saveBarakahProfile = async (profile) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const fallbackKey = `barakah_profile_v1:${user.id}`;
  const payload = {
    ...getDefaultBarakahProfile(),
    ...(profile || {}),
    focus_areas: Array.isArray(profile?.focus_areas) ? profile.focus_areas : getDefaultBarakahProfile().focus_areas,
    user_id: user.id,
  };

  try {
    const { data, error } = await supabase
      .from('barakah_profiles')
      .upsert(payload, { onConflict: 'user_id' })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    localStorage.setItem(fallbackKey, JSON.stringify(payload));
    return payload;
  }
};

export const getTodayBarakahPlan = async (date = getLocalDateString()) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const fallbackKey = `barakah_today_plan_v1:${user.id}`;

  if (!isTableAvailabilityEnabled('daily_barakah_plans')) {
    try {
      const raw = localStorage.getItem(fallbackKey);
      const parsed = raw ? JSON.parse(raw) : {};
      return normalizeTodayPlanShape(parsed?.[date], date);
    } catch {
      return getDefaultTodayPlan(date);
    }
  }

  try {
    const { data, error } = await supabase
      .from('daily_barakah_plans')
      .select('*')
      .eq('user_id', user.id)
      .eq('date', date)
      .maybeSingle();

    setTableAvailability('daily_barakah_plans', true);
    if (error && error.code !== 'PGRST116') throw error;
    if (!data) return getDefaultTodayPlan(date);

    return normalizeTodayPlanShape(data, date);
  } catch (error) {
    if (error?.code === 'PGRST205') {
      setTableAvailability('daily_barakah_plans', false);
    }

    try {
      const raw = localStorage.getItem(fallbackKey);
      const parsed = raw ? JSON.parse(raw) : {};
      return normalizeTodayPlanShape(parsed?.[date], date);
    } catch {
      return getDefaultTodayPlan(date);
    }
  }
};

export const saveTodayBarakahPlan = async (plan, date = getLocalDateString()) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const fallbackKey = `barakah_today_plan_v1:${user.id}`;
  const normalized = normalizeTodayPlanShape(plan, date);
  const payload = {
    user_id: user.id,
    date,
    niyyah: normalized.niyyah,
    priorities: normalized.priorities,
    avoid_distraction: normalized.avoid_distraction,
    notes: normalized.notes,
    plan_items: normalized.plan_items,
  };

  if (!isTableAvailabilityEnabled('daily_barakah_plans')) {
    const raw = localStorage.getItem(fallbackKey);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[date] = normalized;
    localStorage.setItem(fallbackKey, JSON.stringify(parsed));
    return normalized;
  }

  try {
    const { data, error } = await supabase
      .from('daily_barakah_plans')
      .upsert(payload, { onConflict: 'user_id,date' })
      .select()
      .single();

    setTableAvailability('daily_barakah_plans', true);
    if (error) throw error;
    return normalizeTodayPlanShape(data, date);
  } catch (error) {
    if (error?.code === 'PGRST205') {
      setTableAvailability('daily_barakah_plans', false);
    }

    const raw = localStorage.getItem(fallbackKey);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[date] = normalized;
    localStorage.setItem(fallbackKey, JSON.stringify(parsed));
    return normalized;
  }
};

export const createPlanItemCalendarBlock = async ({
  date = getLocalDateString(),
  itemId,
  title,
  startTime = '09:00',
  endTime = '10:00',
  note = '',
  reminderMinutes = null,
  existingEventId = null,
}) => {
  const startIso = normalizePlannerDateTimeValue(`${date}T${String(startTime || '09:00').slice(0, 5)}:00`);
  let endIso = normalizePlannerDateTimeValue(`${date}T${String(endTime || '10:00').slice(0, 5)}:00`);

  const startDate = new Date(startIso);
  const endDate = new Date(endIso);
  if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime()) && endDate <= startDate) {
    endIso = new Date(startDate.getTime() + 60 * 60 * 1000).toISOString();
  }

  const descriptionPayload = {
    source: 'today-plan',
    plan_item_id: itemId || null,
    note: note || null,
  };

  const payload = {
    title: title || 'Barakah focus block',
    description: JSON.stringify(descriptionPayload),
    start_time: startIso,
    end_time: endIso,
    event_type: 'barakah-plan',
    is_recurring: false,
    recurrence_rule: '',
    reminder_minutes: reminderMinutes,
  };

  if (existingEventId) {
    return updatePlannerEvent(existingEventId, payload);
  }

  return createPlannerEvent(payload);
};

export const getPlannedVsActualTimeline = async (date = getLocalDateString()) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const start = `${date}T00:00:00`;
  const end = `${date}T23:59:59`;

  const [plannedEvents, prayersRes, quranLogsRes, bereketRes, dhikrRes, todayPlan] = await Promise.all([
    getPlannerEvents(start, end).catch(() => []),
    supabase.from('prayers').select('*').eq('user_id', user.id).eq('date', date).maybeSingle(),
    supabase.from('quran_daily_logs').select('*').eq('user_id', user.id).eq('date', date),
    supabase
      .from('bereket_logs')
      .select('id,created_at,points_earned,bereket_activities(name_en,name_hr)')
      .eq('user_id', user.id)
      .eq('date', date)
      .order('created_at', { ascending: true }),
    supabase.from('dhikr_counts').select('count').eq('user_id', user.id).eq('date', date).maybeSingle(),
    getTodayBarakahPlan(date).catch(() => getDefaultTodayPlan(date)),
  ]);

  // ── Planned items ──
  const plannedFromPriorities = (todayPlan?.priorities || [])
    .filter((item) => String(item?.title || '').trim())
    .map((item, idx) => ({
      id: item.id || `priority-${idx + 1}`,
      source: 'today-plan',
      type: 'planned',
      title: item.title,
      start_time: item.start_time || '',
      end_time: item.end_time || '',
      blocked_event_id: item.blocked_event_id || null,
      timestamp: item.blocked_start_time || null,
      category: 'priority',
      meta: { blocked: Boolean(item.blocked_event_id), goal_niyyah: item.goal_niyyah || '' },
    }));

  const plannedFromCalendar = (plannedEvents || []).map((event) => ({
    id: event.id || event.event_id,
    source: 'calendar',
    type: 'planned',
    title: event.title,
    duration_minutes: event.end_time ? Math.max(0, Math.round((new Date(event.end_time) - new Date(event.start_time)) / 60000)) : null,
    blocked_event_id: event.id || event.event_id,
    timestamp: event.start_time,
    category: event.event_type || 'general',
    meta: { event_type: event.event_type, completed: Boolean(event.completed) },
  }));

  // ── Actual items ──
  const prayers = prayersRes?.data || {};
  const prayerNameMap = { fajr: 'Fajr', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha' };
  const actualFromPrayers = Object.keys(prayerNameMap)
    .filter((key) => prayers[key] === true)
    .map((key) => ({
      id: `prayer-${key}`,
      source: 'prayers',
      type: 'actual',
      title: `${prayerNameMap[key]} prayer completed`,
      category: 'prayer',
      points_earned: null,
      timestamp: null,
      meta: { prayer: key },
    }));

  const actualFromQuran = (quranLogsRes?.data || []).flatMap((row, idx) => {
    const entries = [];
    if ((row.pages_read || 0) > 0) {
      entries.push({
        id: `quran-read-${idx}`,
        source: 'quran',
        type: 'actual',
        title: `Quran reading: ${row.pages_read} page(s)`,
        category: 'quran-reading',
        points_earned: null,
        timestamp: row.created_at || null,
        meta: { pages_read: row.pages_read },
      });
    }
    const listening = Number(row.listening_minutes ?? row.time_spent ?? 0);
    if (listening > 0) {
      entries.push({
        id: `quran-listen-${idx}`,
        source: 'quran',
        type: 'actual',
        title: `Quran listening: ${listening} min`,
        category: 'quran-listening',
        points_earned: null,
        timestamp: row.created_at || null,
        meta: { listening_minutes: listening },
      });
    }
    return entries;
  });

  const dhikrCount = dhikrRes?.data?.count || 0;
  const actualFromDhikr = dhikrCount > 0
    ? [{
        id: 'dhikr-today',
        source: 'dhikr',
        type: 'actual',
        title: `Dhikr: ${dhikrCount} count`,
        category: 'dhikr',
        points_earned: null,
        timestamp: null,
        meta: { count: dhikrCount },
      }]
    : [];

  // Categorize bereket logs: separate hifz from other bereket
  const actualFromBereket = (bereketRes?.data || []).map((row) => {
    const nameEn = (row?.bereket_activities?.name_en || '').toLowerCase();
    let category = 'bereket';
    if (nameEn.includes('memoriz') || nameEn.includes('hifz') || nameEn.includes('napamet')) category = 'hifz';
    else if (nameEn.includes('quran') && nameEn.includes('read')) category = 'quran-reading';
    else if (nameEn.includes('quran') && nameEn.includes('listen')) category = 'quran-listening';
    else if (nameEn.includes('prayer') || nameEn.includes('namaz') || nameEn.includes('salah')) category = 'prayer';
    else if (nameEn.includes('dhikr') || nameEn.includes('zikr') || nameEn.includes('tasbih')) category = 'dhikr';
    return {
      id: `bereket-${row.id}`,
      source: 'bereket',
      type: 'actual',
      title: row?.bereket_activities?.name_en || 'Barakah activity',
      title_hr: row?.bereket_activities?.name_hr || 'Barakah aktivnost',
      category,
      points_earned: row.points_earned || 0,
      timestamp: row.created_at || null,
      meta: {},
    };
  });

  const planned = [...plannedFromPriorities, ...plannedFromCalendar];
  const actual = [...actualFromPrayers, ...actualFromQuran, ...actualFromDhikr, ...actualFromBereket];
  const mergedTimeline = [...planned, ...actual].sort((a, b) => {
    const aTime = a.timestamp ? new Date(a.timestamp).getTime() : Number.MAX_SAFE_INTEGER;
    const bTime = b.timestamp ? new Date(b.timestamp).getTime() : Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
  });

  // ── Grouped views for side-by-side comparison ──
  const groupItems = (items) => {
    const groups = {};
    for (const item of items) {
      const key = item.category || 'other';
      if (!groups[key]) {
        groups[key] = { category: key, items: [], count: 0, total_minutes: 0, total_points: 0 };
      }
      groups[key].items.push(item);
      groups[key].count += 1;
      if (item.duration_minutes) groups[key].total_minutes += item.duration_minutes;
      if (item.meta?.listening_minutes) groups[key].total_minutes += item.meta.listening_minutes;
      if (item.meta?.pages_read) groups[key].total_minutes += (item.meta.pages_read || 0) * 10; // ~10 min/page estimate
      if (item.meta?.count) groups[key].count = item.meta.count; // dhikr overrides count
      if (item.points_earned) groups[key].total_points += item.points_earned;
      // Compute minutes from start/end time for planned priorities
      if (item.start_time && item.end_time && !item.duration_minutes) {
        const [sh, sm] = String(item.start_time).split(':').map(Number);
        const [eh, em] = String(item.end_time).split(':').map(Number);
        if (Number.isFinite(sh) && Number.isFinite(eh)) {
          const dur = (eh * 60 + (em || 0)) - (sh * 60 + (sm || 0));
          if (dur > 0) groups[key].total_minutes += dur;
        }
      }
    }
    return Object.values(groups);
  };

  return {
    date,
    planned,
    actual,
    mergedTimeline,
    groupedPlanned: groupItems(planned),
    groupedActual: groupItems(actual),
  };
};

export const getQuranBookmarks = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('quran_bookmarks')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const addQuranBookmark = async (bookmark) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('quran_bookmarks')
    .insert([{
      user_id: user.id,
      ...bookmark
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteQuranBookmark = async (bookmarkId) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('quran_bookmarks')
    .delete()
    .eq('id', bookmarkId)
    .eq('user_id', user.id);

  if (error) throw error;
};

export const updateQuranBookmark = async (bookmarkId, updates) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('quran_bookmarks')
    .update(updates)
    .eq('id', bookmarkId)
    .eq('user_id', user.id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getQuranByPage = async (pageNumber, translationId = 'en.sahih', arabicEdition = 'quran-uthmani') => {
  const cacheKey = `page:${pageNumber}:${translationId}:${arabicEdition}`;
  try {
    const [arabicResult, translationResult] = await Promise.all([
      fetchQuranExternal(`page/${pageNumber}/${arabicEdition}`),
      fetchQuranExternal(`page/${pageNumber}/${translationId}`),
    ]);

    if (arabicResult.code === 200 && arabicResult.data && translationResult.code === 200 && translationResult.data) {
      const payload = {
        number: arabicResult.data.number,
        ayahs: arabicResult.data.ayahs.map((ayah, index) => ({
          number: ayah.number,
          text: ayah.text,
          translation: translationResult.data.ayahs[index]?.text || '',
          surah: ayah.surah,
          numberInSurah: ayah.numberInSurah,
          page: ayah.page,
          juz: ayah.juz
        })),
        surahs: arabicResult.data.surahs
      };
      setQuranViewCache(cacheKey, payload);
      return payload;
    }

    throw new Error('Failed to fetch page');
  } catch (error) {
    const message = error?.message || 'Quran page API unavailable';
    console.error('Quran page API error:', message);
    
    // Check cache first
    const cached = getQuranViewCache(cacheKey);
    if (cached) {
      console.error('Quran page API error, using cached content:', message);
      return withDegradedMeta(cached, 'cache', message);
    }

    // CRITICAL: Always return emergency fallback - never throw
    console.error('Quran page API error, using emergency fallback:', message);
    return buildEmergencyPageFallback(pageNumber, message);
  }
};

export const getQuranByJuz = async (juzNumber, translationId = 'en.sahih', arabicEdition = 'quran-uthmani') => {
  const cacheKey = `juz:${juzNumber}:${translationId}:${arabicEdition}`;
  try {
    const [arabicResult, translationResult] = await Promise.all([
      fetchQuranExternal(`juz/${juzNumber}/${arabicEdition}`),
      fetchQuranExternal(`juz/${juzNumber}/${translationId}`),
    ]);

    if (arabicResult.code === 200 && arabicResult.data && translationResult.code === 200 && translationResult.data) {
      const payload = {
        number: arabicResult.data.number,
        ayahs: arabicResult.data.ayahs.map((ayah, index) => ({
          number: ayah.number,
          text: ayah.text,
          translation: translationResult.data.ayahs[index]?.text || '',
          surah: ayah.surah,
          numberInSurah: ayah.numberInSurah,
          page: ayah.page,
          juz: ayah.juz
        })),
        surahs: arabicResult.data.surahs
      };
      setQuranViewCache(cacheKey, payload);
      return payload;
    }

    throw new Error('Failed to fetch juz');
  } catch (error) {
    const message = error?.message || 'Quran juz API unavailable';
    console.error('Quran juz API error:', message);
    
    // Check cache first
    const cached = getQuranViewCache(cacheKey);
    if (cached) {
      console.error('Quran juz API error, using cached content:', message);
      return withDegradedMeta(cached, 'cache', message);
    }

    // CRITICAL: Always return emergency fallback - never throw
    console.error('Quran juz API error, using emergency fallback:', message);
    return buildEmergencyJuzFallback(juzNumber, message);
  }
};

export const getQuranAyahWithCustomTranslation = async (surahNumber, ayahNumber, translationId = 'en.sahih', arabicEdition = null) => {
  const cacheKey = `ayah:${surahNumber}:${ayahNumber}:${translationId}:${arabicEdition || 'quran-uthmani'}`;
  try {
    const fetches = [
      fetchQuranExternal(`ayah/${surahNumber}:${ayahNumber}/ar.alafasy`),
      fetchQuranExternal(`ayah/${surahNumber}:${ayahNumber}/${translationId}`),
    ];
    if (arabicEdition && arabicEdition !== 'quran-uthmani') {
      fetches.push(fetchQuranExternal(`ayah/${surahNumber}:${ayahNumber}/${arabicEdition}`));
    }

    const results = await Promise.all(fetches);
    const arabic = results[0];
    const translation = results[1];
    const textOverride = results[2] || null;

    if (arabic.code === 200 && translation.code === 200) {
      const payload = {
        number: arabic.data.number,
        surah: {
          number: arabic.data.surah.number,
          name: arabic.data.surah.name,
          englishName: arabic.data.surah.englishName,
          englishNameTranslation: arabic.data.surah.englishNameTranslation,
          numberOfAyahs: arabic.data.surah.numberOfAyahs
        },
        numberInSurah: arabic.data.numberInSurah,
        text: (textOverride?.code === 200 ? textOverride.data.text : arabic.data.text),
        translation: translation.data.text,
        audio: arabic.data.audio,
        page: arabic.data.page,
        juz: arabic.data.juz
      };
      setQuranViewCache(cacheKey, payload);
      return payload;
    }

    throw new Error('Failed to fetch ayah');
  } catch (error) {
    const message = error?.message || String(error);
    console.error(`Quran ayah API error: ${message}`);

    // Try surah fallback for environments where /ayah endpoint is blocked by CORS
    // Skip if error is from nested surah call (prevent infinite recursion)
    if (!message.includes('surah fallback')) {
      try {
        const surahData = await getQuranSurahWithCustomTranslation(surahNumber, translationId, arabicEdition);
        
        // If surahData is degraded, use it as-is with ayah filtering
        const targetAyah = surahData?.ayahs?.find((ayah) => ayah.numberInSurah === ayahNumber);

        if (targetAyah) {
          const payload = {
            number: targetAyah.number,
            surah: {
              number: surahData.number,
              name: surahData.name,
              englishName: surahData.englishName,
              englishNameTranslation: surahData.englishNameTranslation,
              numberOfAyahs: surahData.numberOfAyahs,
            },
            numberInSurah: targetAyah.numberInSurah,
            text: targetAyah.text,
            translation: targetAyah.translation,
            audio: targetAyah.audio,
            page: targetAyah.page,
            juz: targetAyah.juz,
          };
          
          // Preserve degraded metadata if surahData was degraded
          if (surahData.degraded) {
            return withDegradedMeta(payload, surahData.degraded_source, surahData.degraded_message);
          }
          
          setQuranViewCache(cacheKey, payload);
          return payload;
        }
      } catch (fallbackError) {
        const fallbackMessage = fallbackError?.message || String(fallbackError);
        console.error(`Quran ayah API surah fallback error: ${fallbackMessage}`);
        // Continue to emergency fallback below
      }
    }

    // Check cache before emergency fallback
    const cached = getQuranViewCache(cacheKey);
    if (cached) {
      console.error(`Quran ayah API error, using cached content: ${message}`);
      return withDegradedMeta(cached, 'cache', message);
    }

    // CRITICAL: Always return emergency fallback - never throw
    console.error(`Quran ayah API error, using emergency fallback: ${message}`);
    return buildEmergencyAyahFallback(surahNumber, ayahNumber, message);
  }
};

export const getQuranSurahWithCustomTranslation = async (surahNumber, translationId = 'en.sahih', arabicEdition = null) => {
  const cacheKey = `surah:${surahNumber}:${translationId}:${arabicEdition || 'quran-uthmani'}`;
  try {
    const fetches = [
      fetchQuranExternal(`surah/${surahNumber}/ar.alafasy`),
      fetchQuranExternal(`surah/${surahNumber}/${translationId}`),
    ];
    if (arabicEdition && arabicEdition !== 'quran-uthmani') {
      fetches.push(fetchQuranExternal(`surah/${surahNumber}/${arabicEdition}`));
    }

    const results = await Promise.all(fetches);
    const arabic = results[0];
    const translation = results[1];
    const textOverride = results[2] || null;

    if (arabic.code === 200 && translation.code === 200) {
      const ayahs = arabic.data.ayahs.map((ayah, index) => ({
        number: ayah.number,
        numberInSurah: ayah.numberInSurah,
        text: (textOverride?.code === 200 && textOverride.data?.ayahs?.[index] ? textOverride.data.ayahs[index].text : ayah.text),
        translation: translation.data.ayahs[index]?.text || '',
        audio: ayah.audio,
        page: ayah.page,
        juz: ayah.juz
      }));

      const payload = {
        number: arabic.data.number,
        name: arabic.data.name,
        englishName: arabic.data.englishName,
        englishNameTranslation: arabic.data.englishNameTranslation,
        numberOfAyahs: arabic.data.numberOfAyahs,
        revelationType: arabic.data.revelationType,
        ayahs
      };
      setQuranViewCache(cacheKey, payload);
      return payload;
    }

    throw new Error('Failed to fetch surah');
  } catch (error) {
    const message = error?.message || 'Quran surah API unavailable';
    console.error('Quran surah API error:', message);
    
    // Check cache first
    const cached = getQuranViewCache(cacheKey);
    if (cached) {
      console.error('Quran surah API error, using cached content:', message);
      return withDegradedMeta(cached, 'cache', message);
    }

    // CRITICAL: Always return emergency fallback - never throw
    console.error('Quran surah API error, using emergency fallback:', message);
    return buildEmergencySurahFallback(surahNumber, message);
  }
};

export const logQuranReadingForBereket = async (surahNumber, pagesRead = 1) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const today = getLocalDateString();

  // Try multiple possible activity names
  let activity = null;
  const possibleNames = ['Read Quran (1 page)', 'Read Quran', "Kur'an čitanje", "Read Qur'an"];
  
  for (const name of possibleNames) {
    const { data } = await supabase
      .from('bereket_activities')
      .select('id, base_points, max_daily_points')
      .eq('name_en', name)
      .maybeSingle();
    if (data) { activity = data; break; }
  }

  // Also try by name_hr
  if (!activity) {
    const { data } = await supabase
      .from('bereket_activities')
      .select('id, base_points, max_daily_points')
      .ilike('name_en', '%quran%read%')
      .maybeSingle();
    if (data) activity = data;
  }

  // Fallback: log directly with a generic "quran" activity_id
  if (!activity) {
    // Just log the points directly
    await logBereketActivity('quran-read', pagesRead * 2, false, 'Read Quran');
    return { points: pagesRead * 2 };
  }

  const { data: todayLogs } = await supabase
    .from('bereket_logs')
    .select('points_earned')
    .eq('user_id', user.id)
    .eq('activity_id', activity.id)
    .eq('date', today);

  const todayPoints = todayLogs?.reduce((sum, log) => sum + log.points_earned, 0) || 0;

  if (activity.max_daily_points && todayPoints >= activity.max_daily_points) {
    return { message: 'Daily limit reached' };
  }

  const pointsToAdd = Math.min(
    activity.base_points * pagesRead,
    activity.max_daily_points ? activity.max_daily_points - todayPoints : activity.base_points * pagesRead
  );

  if (pointsToAdd > 0) {
    await logBereketActivity(activity.id, pointsToAdd, false, 'Read Quran');
  }

  return { points: pointsToAdd };
};

export const logJuzReadingForBereket = async (juzNumber, juzData) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  if (!juzData || !juzData.ayahs) return { message: 'No juz data' };

  const uniqueSurahs = [...new Set(juzData.ayahs.map(ayah => ayah.surah.number))];

  let totalPoints = 0;
  for (const surahNumber of uniqueSurahs) {
    const result = await logQuranReadingForBereket(surahNumber, 1);
    if (result?.points) {
      totalPoints += result.points;
    }
  }

  return { points: totalPoints, surahsMarked: uniqueSurahs.length };
};

export const logQuranListeningForBereket = async (surahNumber) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const today = getLocalDateString();

  // Try multiple possible activity names
  let activity = null;
  const possibleNames = ['Listen to Quran (1 surah)', 'Listen to Quran', "Slušaj Kur'an"];
  
  for (const name of possibleNames) {
    const { data } = await supabase
      .from('bereket_activities')
      .select('id, base_points, max_daily_points')
      .eq('name_en', name)
      .maybeSingle();
    if (data) { activity = data; break; }
  }

  if (!activity) {
    const { data } = await supabase
      .from('bereket_activities')
      .select('id, base_points, max_daily_points')
      .ilike('name_en', '%quran%listen%')
      .maybeSingle();
    if (data) activity = data;
  }

  // Fallback: log directly
  if (!activity) {
    await logBereketActivity('quran-listen', 2, false, 'Listen to Quran');
    return { points: 2 };
  }

  const { data: todayLogs } = await supabase
    .from('bereket_logs')
    .select('points_earned')
    .eq('user_id', user.id)
    .eq('activity_id', activity.id)
    .eq('date', today);

  const todayPoints = todayLogs?.reduce((sum, log) => sum + log.points_earned, 0) || 0;

  if (activity.max_daily_points && todayPoints >= activity.max_daily_points) {
    return { message: 'Daily limit reached' };
  }

  await logBereketActivity(activity.id, activity.base_points, false, 'Listen to Quran');

  return { points: activity.base_points };
};

export const addQuranListeningMinutes = async (minutesToAdd = 1) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const today = getLocalDateString();
  const minutes = Math.max(1, Math.round(Number(minutesToAdd) || 1));

  const emitUpdateEvent = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('bereket:quran-log-updated', {
        detail: { date: today, listening_minutes_added: minutes }
      }));
    }
  };

  const upsertExisting = async (existingRow) => {
    const hasListeningMinutes = Object.prototype.hasOwnProperty.call(existingRow || {}, 'listening_minutes');
    const hasTimeSpent = Object.prototype.hasOwnProperty.call(existingRow || {}, 'time_spent');

    const updatePayload = hasListeningMinutes
      ? { listening_minutes: (Number(existingRow.listening_minutes) || 0) + minutes }
      : hasTimeSpent
      ? { time_spent: (Number(existingRow.time_spent) || 0) + minutes }
      : { time_spent: minutes };

    const { error: updateError } = await supabase
      .from('quran_daily_logs')
      .update(updatePayload)
      .eq('id', existingRow.id);

    if (updateError) throw updateError;
  };

  const { data: existing, error: existingError } = await supabase
    .from('quran_daily_logs')
    .select('*')
    .eq('user_id', user.id)
    .eq('date', today)
    .maybeSingle();

  if (existingError) {
    throw existingError;
  }

  if (existing) {
    await upsertExisting(existing);
    emitUpdateEvent();
    return { success: true };
  }

  const basePayload = {
    user_id: user.id,
    date: today,
    pages_read: 0,
  };

  const firstInsert = await supabase
    .from('quran_daily_logs')
    .insert([{ ...basePayload, time_spent: minutes }])
    .select()
    .single();

  if (!firstInsert.error) {
    emitUpdateEvent();
    return { success: true, data: firstInsert.data };
  }

  const secondInsert = await supabase
    .from('quran_daily_logs')
    .insert([{ ...basePayload, listening_minutes: minutes }])
    .select()
    .single();

  if (!secondInsert.error) {
    emitUpdateEvent();
    return { success: true, data: secondInsert.data };
  }

  // If insert raced with another write, try one last fetch+update
  const { data: racedExisting } = await supabase
    .from('quran_daily_logs')
    .select('*')
    .eq('user_id', user.id)
    .eq('date', today)
    .maybeSingle();

  if (racedExisting) {
    await upsertExisting(racedExisting);
    emitUpdateEvent();
    return { success: true };
  }

  throw secondInsert.error || firstInsert.error || new Error('Failed to save Quran listening minutes');
};

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://wuwihrjwkrqpzaulroiq.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Ind1d2locmp3a3JxcHphdWxyb2lxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzE2Njg2OTEsImV4cCI6MjA4NzI0NDY5MX0.4zRTjTHJ8qQ5yJsrvG52HmHlHNa6UFVWPSmKgNCqgbs';

function hadithProxyHeaders() {
  return {
    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json',
  };
}

export const getHadithBooks = async () => {
  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/hadith-proxy?endpoint=books`,
    { headers: hadithProxyHeaders() }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  return data.books || [];
};

export const getHadithChapters = async (bookSlug) => {
  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/hadith-proxy?endpoint=chapters&book=${bookSlug}`,
    { headers: hadithProxyHeaders() }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  return data.chapters || [];
};

export const getHadithsByBook = async (bookSlug, chapterNumber, page = 1, paginate = 20) => {
  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/hadith-proxy?endpoint=hadiths&book=${bookSlug}&chapter=${chapterNumber}&page=${page}&paginate=${paginate}`,
    { headers: hadithProxyHeaders() }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  const hadiths = data.hadiths || {};
  const list = Array.isArray(hadiths) ? hadiths : (hadiths.data || []);
  return {
    data: list.map(h => ({
      id: h.id,
      hadithNumber: h.hadithNumber,
      englishNarrator: h.englishNarrator || '',
      hadithEnglish: h.hadithEnglish || '',
      hadithArabic: h.hadithArabic || '',
      headingEnglish: h.headingEnglish || '',
      headingArabic: h.headingArabic || '',
      bookSlug: h.bookSlug || bookSlug,
      chapterId: h.chapterId || chapterNumber,
      status: h.status || '',
      volume: h.volume || '',
      book: h.book || null,
      chapter: h.chapter || null,
    })),
    currentPage: hadiths.current_page || 1,
    lastPage: hadiths.last_page || 1,
    total: hadiths.total || list.length,
  };
};

export const searchHadiths = async (query, page = 1) => {
  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/hadith-proxy?endpoint=hadiths&hadithEnglish=${encodeURIComponent(query)}&page=${page}&paginate=25`,
    { headers: hadithProxyHeaders() }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  const hadiths = data.hadiths || {};
  const list = Array.isArray(hadiths) ? hadiths : (hadiths.data || []);
  return {
    data: list.map(h => ({
      id: h.id,
      hadithNumber: h.hadithNumber,
      englishNarrator: h.englishNarrator || '',
      hadithEnglish: h.hadithEnglish || '',
      hadithArabic: h.hadithArabic || '',
      headingEnglish: h.headingEnglish || '',
      bookSlug: h.bookSlug || '',
      status: h.status || '',
      book: h.book || null,
      chapter: h.chapter || null,
    })),
    currentPage: hadiths.current_page || 1,
    lastPage: hadiths.last_page || 1,
    total: hadiths.total || list.length,
  };
};

// Hadith of the Day - returns a curated hadith based on the day
export const getHadithOfTheDay = async () => {
  try {
    // Curated collection of meaningful hadiths from Sahih Bukhari
    const curatedHadiths = [
      {
        hadithNumber: 1,
        bookSlug: 'sahih-bukhari',
        bookName: 'Sahih al-Bukhari',
        englishNarrator: "Narrated 'Umar bin Al-Khattab",
        hadithEnglish: "I heard Allah's Messenger (ﷺ) saying, 'The reward of deeds depends upon the intentions and every person will get the reward according to what he has intended.'",
        hadithArabic: "إِنَّمَا الأَعْمَالُ بِالنِّيَّاتِ، وَإِنَّمَا لِكُلِّ امْرِئٍ مَا نَوَى",
        topic: 'intentions'
      },
      {
        hadithNumber: 13,
        bookSlug: 'sahih-bukhari',
        bookName: 'Sahih al-Bukhari',
        englishNarrator: "Narrated Anas",
        hadithEnglish: "The Prophet (ﷺ) said, 'None of you will have faith till he wishes for his (Muslim) brother what he likes for himself.'",
        hadithArabic: "لَا يُؤْمِنُ أَحَدُكُمْ حَتَّى يُحِبَّ لِأَخِيهِ مَا يُحِبُّ لِنَفْسِهِ",
        topic: 'brotherhood'
      },
      {
        hadithNumber: 6018,
        bookSlug: 'sahih-bukhari',
        bookName: 'Sahih al-Bukhari',
        englishNarrator: "Narrated Abu Huraira",
        hadithEnglish: "The Prophet (ﷺ) said, 'Whoever believes in Allah and the Last Day should talk what is good or keep quiet, and whoever believes in Allah and the Last Day should not hurt his neighbor.'",
        hadithArabic: "مَنْ كَانَ يُؤْمِنُ بِاللَّهِ وَالْيَوْمِ الآخِرِ فَلْيَقُلْ خَيْرًا أَوْ لِيَصْمُتْ",
        topic: 'speech'
      },
      {
        hadithNumber: 6137,
        bookSlug: 'sahih-bukhari',
        bookName: 'Sahih al-Bukhari',
        englishNarrator: "Narrated Abu Huraira",
        hadithEnglish: "The Prophet (ﷺ) said, 'The strong is not the one who overcomes the people by his strength, but the strong is the one who controls himself while in anger.'",
        hadithArabic: "لَيْسَ الشَّدِيدُ بِالصُّرَعَةِ، إِنَّمَا الشَّدِيدُ الَّذِي يَمْلِكُ نَفْسَهُ عِنْدَ الْغَضَبِ",
        topic: 'anger'
      },
      {
        hadithNumber: 6116,
        bookSlug: 'sahih-bukhari',
        bookName: 'Sahih al-Bukhari',
        englishNarrator: "Narrated 'Abdullah bin 'Amr",
        hadithEnglish: "The Prophet (ﷺ) said, 'A Muslim is the one who avoids harming Muslims with his tongue and hands.'",
        hadithArabic: "الْمُسْلِمُ مَنْ سَلِمَ الْمُسْلِمُونَ مِنْ لِسَانِهِ وَيَدِهِ",
        topic: 'character'
      },
      {
        hadithNumber: 5673,
        bookSlug: 'sahih-bukhari',
        bookName: 'Sahih al-Bukhari',
        englishNarrator: "Narrated Abu Huraira",
        hadithEnglish: "The Prophet (ﷺ) said, 'Allah says: I am just as My slave thinks I am, and I am with him if He remembers Me.'",
        hadithArabic: "أَنَا عِنْدَ ظَنِّ عَبْدِي بِي، وَأَنَا مَعَهُ إِذَا ذَكَرَنِي",
        topic: 'remembrance'
      },
      {
        hadithNumber: 6463,
        bookSlug: 'sahih-bukhari',
        bookName: 'Sahih al-Bukhari',
        englishNarrator: "Narrated Abu Huraira",
        hadithEnglish: "The Prophet (ﷺ) said, 'The most beloved deed to Allah is the most regular and constant even if it were little.'",
        hadithArabic: "أَحَبُّ الأَعْمَالِ إِلَى اللَّهِ أَدْوَمُهَا وَإِنْ قَلَّ",
        topic: 'consistency'
      },
      {
        hadithNumber: 6502,
        bookSlug: 'sahih-bukhari',
        bookName: 'Sahih al-Bukhari',
        englishNarrator: "Narrated Abu Huraira",
        hadithEnglish: "The Prophet (ﷺ) said, 'Allah said: I will declare war against him who shows hostility to a pious worshipper of Mine.'",
        hadithArabic: "مَنْ عَادَى لِي وَلِيًّا فَقَدْ آذَنْتُهُ بِالْحَرْبِ",
        topic: 'piety'
      },
      {
        hadithNumber: 52,
        bookSlug: 'sahih-bukhari',
        bookName: 'Sahih al-Bukhari',
        englishNarrator: "Narrated An-Nu'man bin Bashir",
        hadithEnglish: "The Prophet (ﷺ) said, 'Both legal and illegal things are evident but in between them there are doubtful things. So whoever forsakes those doubtful things lest he may commit a sin, will definitely avoid what is clearly illegal.'",
        hadithArabic: "الْحَلاَلُ بَيِّنٌ، وَالْحَرَامُ بَيِّنٌ، وَبَيْنَهُمَا أُمُورٌ مُشْتَبِهَاتٌ",
        topic: 'halal-haram'
      },
      {
        hadithNumber: 6017,
        bookSlug: 'sahih-bukhari',
        bookName: 'Sahih al-Bukhari',
        englishNarrator: "Narrated Abu Huraira",
        hadithEnglish: "The Prophet (ﷺ) said, 'A word of wisdom is the lost property of a believer, so wherever he finds it he has a better right to it.'",
        hadithArabic: "الْكَلِمَةُ الْحِكْمَةُ ضَالَّةُ الْمُؤْمِنِ",
        topic: 'wisdom'
      },
      {
        hadithNumber: 5997,
        bookSlug: 'sahih-bukhari',
        bookName: 'Sahih al-Bukhari',
        englishNarrator: "Narrated Abu Huraira",
        hadithEnglish: "The Prophet (ﷺ) said, 'He who does not thank the people does not thank Allah.'",
        hadithArabic: "لَا يَشْكُرُ اللَّهَ مَنْ لَا يَشْكُرُ النَّاسَ",
        topic: 'gratitude'
      },
      {
        hadithNumber: 11,
        bookSlug: 'sahih-bukhari',
        bookName: 'Sahih al-Bukhari',
        englishNarrator: "Narrated Abu Musa",
        hadithEnglish: "The Prophet (ﷺ) was asked, 'Which Islam is the best?' He replied, 'The one from whose tongue and hand the Muslims are safe.'",
        hadithArabic: "أَيُّ الإِسْلاَمِ أَفْضَلُ قَالَ مَنْ سَلِمَ الْمُسْلِمُونَ مِنْ لِسَانِهِ وَيَدِهِ",
        topic: 'islam'
      },
      {
        hadithNumber: 6011,
        bookSlug: 'sahih-bukhari',
        bookName: 'Sahih al-Bukhari',
        englishNarrator: "Narrated An-Nu'man bin Bashir",
        hadithEnglish: "The Prophet (ﷺ) said, 'The example of the believers in their mutual love, mercy and compassion is like that of a body; if one part of it feels pain, the whole body responds with sleeplessness and fever.'",
        hadithArabic: "مَثَلُ الْمُؤْمِنِينَ فِي تَوَادِّهِمْ وَتَرَاحُمِهِمْ وَتَعَاطُفِهِمْ مَثَلُ الْجَسَدِ",
        topic: 'unity'
      },
      {
        hadithNumber: 6484,
        bookSlug: 'sahih-bukhari',
        bookName: 'Sahih al-Bukhari',
        englishNarrator: "Narrated Ibn Abbas",
        hadithEnglish: "The Prophet (ﷺ) said, 'There are two blessings which many people lose: Health and free time for doing good.'",
        hadithArabic: "نِعْمَتَانِ مَغْبُونٌ فِيهِمَا كَثِيرٌ مِنَ النَّاسِ الصِّحَّةُ وَالْفَرَاغُ",
        topic: 'time'
      }
    ];

    // Select hadith based on day of year for consistency
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 0);
    const diff = now - startOfYear;
    const oneDay = 1000 * 60 * 60 * 24;
    const dayOfYear = Math.floor(diff / oneDay);
    
    const index = dayOfYear % curatedHadiths.length;
    return curatedHadiths[index];
  } catch (error) {
    console.error('Error getting hadith of the day:', error);
    return null;
  }
};

export const searchQuranByKeyword = async (keyword, translationId = 'en.sahih') => {
  try {
    const result = await fetchQuranExternal(`search/${encodeURIComponent(keyword)}/all/${translationId}`);

    if (result.code === 200 && result.data) {
      return {
        count: result.data.count,
        matches: result.data.matches.map(match => ({
          number: match.number,
          text: match.text,
          surah: match.surah,
          numberInSurah: match.numberInSurah,
          edition: match.edition
        }))
      };
    }

    throw new Error('Failed to search Quran');
  } catch (error) {
    console.error('Quran search API error:', error);
    throw error;
  }
};

export const searchQuranInArabic = async (keyword) => {
  try {
    const result = await fetchQuranExternal(`search/${encodeURIComponent(keyword)}/all/quran-simple`);

    if (result.code === 200 && result.data) {
      return {
        count: result.data.count,
        matches: result.data.matches.map(match => ({
          number: match.number,
          text: match.text,
          surah: match.surah,
          numberInSurah: match.numberInSurah
        }))
      };
    }

    throw new Error('Failed to search Quran');
  } catch (error) {
    console.error('Quran search API error:', error);
    throw error;
  }
};

export const saveSearchHistory = async (searchType, query, resultsCount) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('search_history')
    .insert([{
      user_id: user.id,
      search_type: searchType,
      query: query,
      results_count: resultsCount
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getSearchHistory = async (limit = 20) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('search_history')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data || [];
};


// ==================== READ AYAHS SYNC ====================

export const getReadAyahs = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('quran_reading_progress')
    .select('*')
    .eq('user_id', user.id);

  if (error) {
    console.error('Error fetching read ayahs:', error);
    return [];
  }
  
  return normalizeQuranProgressToAyahList(data || []);
};

export const markAyahAsReadInDB = async (surahNumber, ayahNumber) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Check if already marked (supports both schema variants)
  const { data: existingRows, error: existingError } = await supabase
    .from('quran_reading_progress')
    .select('*')
    .eq('user_id', user.id)
    .eq('surah_number', surahNumber);

  if (existingError) {
    throw existingError;
  }

  const alreadyExists = (existingRows || []).some((row) => {
    const singleAyah = Number(row?.ayah_number);
    if (Number.isFinite(singleAyah)) {
      return singleAyah === ayahNumber;
    }
    const ayahStart = Number(row?.ayah_start);
    const ayahEnd = Number(row?.ayah_end);
    if (Number.isFinite(ayahStart) && Number.isFinite(ayahEnd)) {
      return ayahNumber >= ayahStart && ayahNumber <= ayahEnd;
    }
    return false;
  });

  if (alreadyExists) {
    return { alreadyRead: true };
  }

  const modernPayload = {
    user_id: user.id,
    surah_number: surahNumber,
    ayah_number: ayahNumber,
  };

  const legacyPayload = {
    user_id: user.id,
    surah_number: surahNumber,
    ayah_start: ayahNumber,
    ayah_end: ayahNumber,
    date: getLocalDateString(),
    is_read: true,
    is_memorized: true,
  };

  const tryInsert = async (payload) => {
    const { data, error } = await supabase
      .from('quran_reading_progress')
      .insert([payload])
      .select()
      .single();
    return { data, error };
  };

  const firstPayload = quranReadingProgressSchemaPreference === 'modern' ? modernPayload : legacyPayload;
  const secondPayload = quranReadingProgressSchemaPreference === 'modern' ? legacyPayload : modernPayload;

  const firstTry = await tryInsert(firstPayload);
  if (!firstTry.error) {
    return firstTry.data;
  }

  const secondTry = await tryInsert(secondPayload);
  if (!secondTry.error) {
    quranReadingProgressSchemaPreference = quranReadingProgressSchemaPreference === 'modern' ? 'legacy' : 'modern';
    return secondTry.data;
  }

  throw secondTry.error || firstTry.error;
};

export const unmarkAyahAsReadInDB = async (surahNumber, ayahNumber) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const deleteModern = async () => supabase
    .from('quran_reading_progress')
    .delete()
    .eq('user_id', user.id)
    .eq('surah_number', surahNumber)
    .eq('ayah_number', ayahNumber);

  const deleteLegacy = async () => supabase
    .from('quran_reading_progress')
    .delete()
    .eq('user_id', user.id)
    .eq('surah_number', surahNumber)
    .lte('ayah_start', ayahNumber)
    .gte('ayah_end', ayahNumber);

  const firstDelete = quranReadingProgressSchemaPreference === 'modern' ? deleteModern : deleteLegacy;
  const secondDelete = quranReadingProgressSchemaPreference === 'modern' ? deleteLegacy : deleteModern;

  const firstResult = await firstDelete();
  if (!firstResult.error) {
    return { success: true };
  }

  const secondResult = await secondDelete();
  if (secondResult.error) throw secondResult.error;

  quranReadingProgressSchemaPreference = quranReadingProgressSchemaPreference === 'modern' ? 'legacy' : 'modern';
  return { success: true };
};


// ============================================
// NOTIFICATION SETTINGS & PUSH (via Supabase direct)
// ============================================

const VAPID_PUBLIC_KEY = 'BJeAz378TmfPOf96DvP5hdfRhQCvWT9qJxcNgOtq75KFWVgVwfjcRp33VYLgNMN5qMMAWyy5oM9YxkWo5i_slxY';

const getSupabaseUserId = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  return user.id;
};

export const getVapidPublicKey = async () => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const resp = await fetch(`${backendUrl}/api/notifications/vapid-public-key`);
    if (resp.ok) {
      const data = await resp.json();
      if (data.key) return data.key;
    }
  } catch (error) {
    console.warn('Failed to fetch backend VAPID key, using fallback key', error);
  }
  return VAPID_PUBLIC_KEY;
};

export const getUserNotificationSettings = async () => {
  const userId = await getSupabaseUserId();
  
  const { data, error } = await supabase
    .from('notification_settings')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  
  if (error) {
    console.warn('notification_settings query failed:', error.message);
    return {
      push_enabled: false,
      weekly_report_enabled: false,
      prayer_reminders: false,
      dhikr_reminders: false,
      verse_of_day: false,
      reflection_reminders: false,
      prayer_reminder_time: '04:30',
      dhikr_reminder_time: '09:00',
      verse_reminder_time: '07:00',
      reflection_reminder_time: '20:00',
    };
  }

  const normalized = {
    push_enabled: false,
    weekly_report_enabled: false,
    prayer_reminders: false,
    dhikr_reminders: false,
    verse_of_day: false,
    reflection_reminders: false,
    prayer_reminder_time: '04:30',
    dhikr_reminder_time: '09:00',
    verse_reminder_time: '07:00',
    reflection_reminder_time: '20:00',
    ...data,
  };

  // Backward compatibility:
  // - existing users had local time saved directly
  // - new saves store UTC for reliable backend scheduling
  const timesStoredAsUtc = getNotificationUtcStorageFlag();
  if (!timesStoredAsUtc && data) {
    // One-time silent migration: convert existing local reminder times to UTC in DB.
    const migrationPayload = {};
    NOTIFICATION_TIME_KEYS.forEach((key) => {
      if (isValidTimeValue(data[key])) {
        migrationPayload[key] = convertLocalTimeToUtc(data[key]);
      }
    });

    if (Object.keys(migrationPayload).length > 0) {
      try {
        await supabase
          .from('notification_settings')
          .upsert({
            user_id: userId,
            ...migrationPayload,
            updated_at: new Date().toISOString(),
          }, { onConflict: 'user_id' });
        setNotificationUtcStorageFlag();
      } catch (migrationError) {
        console.warn('Notification time UTC migration skipped:', migrationError?.message || migrationError);
      }
    }
  }

  if (timesStoredAsUtc) {
    NOTIFICATION_TIME_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(data || {}, key) && isValidTimeValue(normalized[key])) {
        normalized[key] = convertUtcTimeToLocal(normalized[key]);
      }
    });
  }

  return normalized;
};

export const updateNotificationSettings = async (settings) => {
  const userId = await getSupabaseUserId();

  const settingsToPersist = { ...settings };
  let convertedTimeToUtc = false;

  NOTIFICATION_TIME_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(settingsToPersist, key) && isValidTimeValue(settingsToPersist[key])) {
      settingsToPersist[key] = convertLocalTimeToUtc(settingsToPersist[key]);
      convertedTimeToUtc = true;
    }
  });

  if (convertedTimeToUtc) {
    setNotificationUtcStorageFlag();
  }
  
  const row = {
    user_id: userId,
    ...settingsToPersist,
    updated_at: new Date().toISOString(),
  };
  
  const { data, error } = await supabase
    .from('notification_settings')
    .upsert(row, { onConflict: 'user_id' })
    .select()
    .single();
  
  if (error) {
    console.error('Failed to update notification settings:', error.message);
    throw new Error('Failed to update notification settings');
  }
  
  return data;
};

export const savePushSubscription = async (subscription) => {
  const userId = await getSupabaseUserId();
  const subscriptionJSON = subscription.toJSON();
  
  const row = {
    user_id: userId,
    endpoint: subscriptionJSON.endpoint,
    p256dh: subscriptionJSON.keys.p256dh,
    auth: subscriptionJSON.keys.auth,
    updated_at: new Date().toISOString(),
  };
  
  const { data, error } = await supabase
    .from('push_subscriptions')
    .upsert(row, { onConflict: 'user_id,endpoint' })
    .select()
    .single();
  
  if (error) {
    console.error('Failed to save push subscription:', error.message);
    throw new Error('Failed to save push subscription');
  }
  
  return data;
};

export const deletePushSubscription = async (endpoint) => {
  const userId = await getSupabaseUserId();
  
  const { error } = await supabase
    .from('push_subscriptions')
    .delete()
    .eq('user_id', userId)
    .eq('endpoint', endpoint);
  
  if (error) {
    console.error('Failed to delete push subscription:', error.message);
  }
  
  return { success: true };
};

export const sendTestPush = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const response = await fetch(`${BACKEND_URL}/api/notifications/send-test-push`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.detail || 'Failed to send test push');
  }

  return payload;
};

// Notification category to URL mapping
const NOTIFICATION_ROUTES = {
  prayer: '/prayer-times',
  dhikr: '/dhikr',
  quran: '/quran',
  journal: '/journal',
  bereket: '/bereket',
  planner: '/planner',
  reflection: '/journal',
  dua: '/dua',
  hadith: '/hadith',
  hifz: '/hifz',
  default: '/dashboard',
};

// Get navigation URL for a notification category
export const getNotificationRoute = (category) => {
  return NOTIFICATION_ROUTES[category] || NOTIFICATION_ROUTES.default;
};

// Send notification for activity with proper routing
export const sendActivityNotification = async (activityName, pointsEarned) => {
  try {
    // Check if user has notifications enabled
    const settings = await getUserNotificationSettings();
    if (!settings?.push_enabled) {
      console.log('Push notifications are disabled');
      return { success: false, reason: 'disabled' };
    }

    // Check if browser supports notifications
    if (!('serviceWorker' in navigator) || !('Notification' in window)) {
      console.log('Notifications not supported');
      return { success: false, reason: 'not_supported' };
    }

    // Check permission
    if (Notification.permission !== 'granted') {
      console.log('Notification permission not granted');
      return { success: false, reason: 'no_permission' };
    }

    const registration = await navigator.serviceWorker.ready;
    
    // Motivational messages
    const messages = [
      `Ma sha Allah! +${pointsEarned} bodova za "${activityName}"`,
      `Bereket! +${pointsEarned} bodova - ${activityName}`,
      `Allahu Akbar! +${pointsEarned} - ${activityName}`,
      `Barak Allahu Feek! +${pointsEarned} za ${activityName}`,
    ];
    
    const randomMessage = messages[Math.floor(Math.random() * messages.length)];
    
    // Determine category from activity name for routing
    const categoryMap = {
      'prayer': 'prayer', 'namaz': 'prayer', 'fajr': 'prayer', 'dhuhr': 'prayer', 
      'asr': 'prayer', 'maghrib': 'prayer', 'isha': 'prayer', 'sabah': 'prayer',
      'quran': 'quran', 'kur': 'quran', 'read': 'quran', 'listen': 'quran',
      'dhikr': 'dhikr', 'zikr': 'dhikr', 'tasbih': 'dhikr',
      'journal': 'journal', 'dnevnik': 'journal', 'reflection': 'journal',
      'sadaqah': 'bereket', 'sadaka': 'bereket', 'good deed': 'bereket',
      'dua': 'dua', 'hadith': 'hadith', 'hifz': 'hifz',
    };
    
    const lowerName = activityName.toLowerCase();
    let category = 'bereket';
    for (const [keyword, cat] of Object.entries(categoryMap)) {
      if (lowerName.includes(keyword)) { category = cat; break; }
    }
    
    const navUrl = getNotificationRoute(category);

    await registration.showNotification('Bereket Activity', {
      body: randomMessage,
      tag: `bereket-activity-${Date.now()}`,
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      vibrate: [100, 50, 100],
      data: {
        type: 'activity',
        category,
        url: navUrl,
        activityName,
        pointsEarned
      }
    });
    
    return { success: true, message: 'Notification sent' };
  } catch (error) {
    console.error('Failed to send activity notification:', error);
    return { success: false, error: error.message };
  }
};


// Send weekly report email (calls backend)
export const sendWeeklyReportEmail = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  const response = await fetch(`${BACKEND_URL}/api/notifications/send-weekly-report`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ user_id: session.user.id }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to send weekly report');
  }
  
  return response.json();
};

// Send test email (calls backend)
export const sendTestEmail = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  const response = await fetch(`${BACKEND_URL}/api/notifications/send-test-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to send test email');
  }
  
  return response.json();
};

export const getAdminOverview = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const envBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
  const candidateUrls = [];

  if (envBackendUrl) {
    candidateUrls.push(`${envBackendUrl}/api/admin/overview`);
  }

  if (typeof window !== 'undefined') {
    candidateUrls.push('/api/admin/overview');

    if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
      candidateUrls.push('http://127.0.0.1:8001/api/admin/overview');
    }
  }

  const uniqueUrls = [...new Set(candidateUrls)];
  let lastError = 'Failed to load admin overview';

  for (const url of uniqueUrls) {
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      const text = await response.text();
      const contentType = response.headers.get('content-type') || '';
      const looksLikeHtml = /^\s*</.test(text);
      const looksLikeJson = contentType.includes('application/json') || /^[\[{]/.test(text.trim());

      if (!response.ok) {
        if (looksLikeJson) {
          const parsed = JSON.parse(text);
          lastError = parsed?.detail || parsed?.message || `Admin overview request failed (${response.status})`;
        } else {
          lastError = `Admin overview request failed (${response.status})`;
        }
        continue;
      }

      if (looksLikeHtml) {
        lastError = `Admin overview endpoint returned HTML instead of JSON (${url})`;
        continue;
      }

      if (!looksLikeJson) {
        lastError = `Admin overview endpoint returned unsupported response (${url})`;
        continue;
      }

      return JSON.parse(text);
    } catch (error) {
      lastError = error.message || 'Failed to load admin overview';
    }
  }

  throw new Error(lastError);
};


// ==================== SCHEDULED NOTIFICATIONS ====================

const SCHEDULED_NOTIF_MESSAGES = {
  prayer: {
    titles: ['Vrijeme za namaz', 'Prayer Time'],
    bodies_hr: [
      'Ne zaboravi namaz! Allah voli redovite.',
      'Namaz je stub vjere. Ustani i klanjaj!',
      'Vrijeme je za razgovor s Allahom.',
    ],
    bodies_en: [
      'Don\'t forget your prayer! Allah loves consistency.',
      'Prayer is the pillar of faith. Rise and pray!',
      'It\'s time to talk to Allah.',
    ],
    category: 'prayer',
  },
  dhikr: {
    titles: ['Vrijeme za zikr', 'Dhikr Time'],
    bodies_hr: [
      'SubhanAllah, Alhamdulillah, Allahu Akbar - započni dan zikrom!',
      'Sjeti se Allaha. Srca se zikrom smiruju.',
      'Jutarnji zikr donosi bereket u dan.',
    ],
    bodies_en: [
      'SubhanAllah, Alhamdulillah, Allahu Akbar - start your day with dhikr!',
      'Remember Allah. Hearts find peace in remembrance.',
      'Morning dhikr brings blessing to your day.',
    ],
    category: 'dhikr',
  },
  verse: {
    titles: ['Ajet dana', 'Verse of the Day'],
    bodies_hr: [
      'Otvori Kur\'an i pročitaj danas svoj ajet.',
      'Kur\'an je lijek za dušu. Čitaj i razmišljaj.',
      'Svaki ajet nosi poruku za tebe.',
    ],
    bodies_en: [
      'Open the Quran and read your verse today.',
      'The Quran is medicine for the soul. Read and reflect.',
      'Every verse carries a message for you.',
    ],
    category: 'quran',
  },
  reflection: {
    titles: ['Vrijeme za refleksiju', 'Reflection Time'],
    bodies_hr: [
      'Kako je prošao tvoj dan? Zapiši misli u dnevnik.',
      'Večernja refleksija - zahvali Allahu na današnjem danu.',
      'Napiši par redova u dnevnik. Svaka misao je ibret.',
    ],
    bodies_en: [
      'How was your day? Write your thoughts in the journal.',
      'Evening reflection - thank Allah for today.',
      'Write a few lines in your journal. Every thought is a lesson.',
    ],
    category: 'journal',
  },
};

// Send a scheduled notification
export const sendScheduledNotification = async (type, lang = 'hr') => {
  try {
    if (!('serviceWorker' in navigator) || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    const config = SCHEDULED_NOTIF_MESSAGES[type];
    if (!config) return;

    const registration = await navigator.serviceWorker.ready;
    const bodies = lang === 'hr' ? config.bodies_hr : config.bodies_en;
    const title = lang === 'hr' ? config.titles[0] : config.titles[1];
    const body = bodies[Math.floor(Math.random() * bodies.length)];
    const navUrl = getNotificationRoute(config.category);

    await registration.showNotification(title, {
      body,
      tag: `bereket-${type}-${new Date().toDateString()}`,
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      vibrate: [200, 100, 200],
      requireInteraction: true,
      data: {
        type: 'scheduled',
        category: config.category,
        url: navUrl,
      },
    });

    return { success: true };
  } catch (error) {
    console.error(`Failed to send ${type} notification:`, error);
    return { success: false };
  }
};

// Notification scheduler - runs in the app to check every 30 seconds
let schedulerInterval = null;
let lastChecked = {};

export const startNotificationScheduler = (settings, lang = 'hr') => {
  stopNotificationScheduler();
  
  if (!settings) return;

  // Also persist last checked state to avoid duplicates across restarts
  try {
    const saved = localStorage.getItem('bereket_notif_checked');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.date === new Date().toDateString()) {
        lastChecked = parsed.checks || {};
      }
    }
  } catch (error) {
    console.warn('Failed to restore notification scheduler cache', error);
  }
  
  const doCheck = () => {
    const now = new Date();
    const h = now.getHours();
    const m = now.getMinutes();
    const currentMinutes = h * 60 + m;
    const todayKey = now.toDateString();
    
    const checks = [
      { enabled: settings.prayer_reminders, time: settings.prayer_reminder_time || '04:30', type: 'prayer' },
      { enabled: settings.dhikr_reminders, time: settings.dhikr_reminder_time || '09:00', type: 'dhikr' },
      { enabled: settings.verse_of_day, time: settings.verse_reminder_time || '07:00', type: 'verse' },
      { enabled: settings.reflection_reminders, time: settings.reflection_reminder_time || '20:00', type: 'reflection' },
    ];
    
    checks.forEach(({ enabled, time, type }) => {
      const checkKey = `${type}-${todayKey}`;
      if (!enabled || lastChecked[checkKey]) return;
      
      const [th, tm] = time.split(':').map(Number);
      const targetMinutes = th * 60 + tm;
      
      // ±1 minute window to avoid missing the exact second
      if (Math.abs(currentMinutes - targetMinutes) <= 1) {
        lastChecked[checkKey] = true;
        sendScheduledNotification(type, lang);
        // Persist
        try {
          localStorage.setItem('bereket_notif_checked', JSON.stringify({ date: todayKey, checks: lastChecked }));
        } catch (error) {
          console.warn('Failed to persist notification scheduler cache', error);
        }
      }
    });
    
    // Clean old keys
    Object.keys(lastChecked).forEach(key => {
      if (!key.endsWith(todayKey)) delete lastChecked[key];
    });
  };

  // Check immediately on start
  doCheck();
  
  schedulerInterval = setInterval(doCheck, 30000); // Check every 30 seconds
};

export const stopNotificationScheduler = () => {
  if (schedulerInterval) {
    clearInterval(schedulerInterval);
    schedulerInterval = null;
  }
};
